// In Results.tsx

import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
// import { useSelector } from 'react-redux'; // useAppSelector is preferred
import {
    Grid,
    Typography,
    Paper,
    Card,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Box,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import { RootState } from '../../../../reduxService'; // Adatta
import { useTranslation } from 'react-i18next';
import TireAndLayers, { ILayerData, IGeogridInfo } from './TireAndLayersPaved'; // Importa TireAndLayersPaved
import { MAX_CANVAS_HEIGHT } from './canvasConstants';
import { unit as unitEnum, EProjectType } from '../../../enum/project'; // Adatta, Import EProjectType
// import { selectDynamicProject } from '../../../Dynamic/store/project/projectslice'; // No longer needed directly if using dynamicProject from RootState
// import { selectProjectInfo } from '../../../store/projectInfo/projectInfoSlice'; // No longer needed if projectInfo comes from dynamicProject
import { useAppSelector, useAppDispatch } from '../../../../reduxService/hooks';
import './Results.scss';
import { logDebug, logError, logWarn, LogTag } from '../../../utils/utils';
import html2canvas from 'html2canvas';
import { pdf } from '@react-pdf/renderer';
// Use IContainerYardData as suggested by error, and ensure IDynamicProjectOutputData is correctly defined in IApiResponse.ts
import { IContainerYardData, IDynamicProjectOutputData, IParkingDeckData, IPavedData, IAASHTOResults } from '../../../api/IApiResponse'; // Added IParkingDeckData, IPavedData, IAASHTOResults
import { convertFieldValue } from '../../../services/UnitConversionService'; // Import convertFieldValue
import convert from 'convert-units'; // For direct unit conversion in Results display
import { useFormValues } from '../../../contexts/FormValuesContext'; // 🔧 FIX: Import for real-time slider updates
import { setPavedCalculatedDesignThickness } from '../../../Dynamic/store/project/projectslice';
import CustomTable from '../../../components/Layout/CustomTable/CustomTable';
import { PavedCalculationService } from '../../../services/calculation/PavedCalculationService';

const Results_Paved = () => {
    const rawOutput = useAppSelector((state: RootState) => state.Dynamic.results.data?.outputData as IDynamicProjectOutputData | undefined);
    const aashtoResults = useAppSelector((state: RootState) => state.Dynamic.project.data?.aashtoResults as IAASHTOResults | undefined);
    // const projectInfo = useAppSelector(selectProjectInfo); // Sourced from dynamicProject now
    const dynamicProject = useAppSelector((state: RootState) => state.Dynamic.project); // Get the whole dynamicProject state
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    // Canvas capture state for PDF export
    const [capturedCanvasImages, setCapturedCanvasImages] = useState<{ [key: string]: string }>({});

    // PDF export loading states
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const [isExportingGeogridPdf, setIsExportingGeogridPdf] = useState(false);
    const [pdfExportError, setPdfExportError] = useState<string | null>(null);
    const [pdfExportSuccess, setPdfExportSuccess] = useState<string | null>(null);

    // Get thickness inputs from form data instead of local state
    const projectData = dynamicProject.data?.projectInfo?.data as IPavedData;
    const esalMode = projectData?.trafficLoad?.esalCalculationMode || 'manual';
    const esalFinalValue = projectData?.trafficLoad?.esalFinalValue;

    // 🔧 FIX: Read slider values from FormValuesContext for REAL-TIME Canvas updates
    // FormValuesContext is synced with Formik in real-time via useFormikSyncToContext
    // Redux is only updated on form SUBMIT, so reading from Redux gives stale values
    // This fixes both Metric and Imperial modes - sliders update Canvas immediately
    const { formValues } = useFormValues();
    const formProjectData = formValues as unknown as IPavedData | undefined;

    // Read enable flags from FormValuesContext (real-time) with Redux fallback
    const enableSurfaceDesignFromForm = formProjectData?.surfaceDesign?.enableSurfaceDesign ?? projectData?.surfaceDesign?.enableSurfaceDesign;
    const enableBaseDesignFromForm = formProjectData?.baseDesign?.enableBaseDesign ?? projectData?.baseDesign?.enableBaseDesign;
    const enableSubbaseDesignFromForm = formProjectData?.subbaseDesign?.enableSubbaseDesign ?? projectData?.subbaseDesign?.enableSubbaseDesign;

    // 🔧 FIX: Read slider values from FormValuesContext for real-time Canvas updates
    // Surface Design slider values (real-time from form, fallback to Redux)
    const surfaceDesignBaseThicknessFromForm = formProjectData?.surfaceDesign?.baseThickness ?? projectData?.surfaceDesign?.baseThickness;
    const surfaceDesignSubbaseThicknessFromForm = formProjectData?.surfaceDesign?.subbaseThickness ?? projectData?.surfaceDesign?.subbaseThickness;
    // Base Design slider values (real-time from form, fallback to Redux)
    const baseDesignSurfaceThicknessFromForm = formProjectData?.baseDesign?.surfaceThickness ?? projectData?.baseDesign?.surfaceThickness;
    const baseDesignSubbaseThicknessFromForm = formProjectData?.baseDesign?.subbaseThickness ?? projectData?.baseDesign?.subbaseThickness;
    // Subbase Design slider values (real-time from form, fallback to Redux)
    const subbaseDesignSurfaceThicknessFromForm = formProjectData?.subbaseDesign?.surfaceThickness ?? projectData?.subbaseDesign?.surfaceThickness;
    const subbaseDesignBaseThicknessFromForm = formProjectData?.subbaseDesign?.baseThickness ?? projectData?.subbaseDesign?.baseThickness;

    // Check if design analysis are enabled via checkboxes (now reading from FormValuesContext)
    const isSurfaceDesignEnabled = enableSurfaceDesignFromForm === 'yes';
    const isBaseDesignEnabled = enableBaseDesignFromForm === 'yes';
    const isSubbaseDesignEnabled = enableSubbaseDesignFromForm === 'yes';

    // 🔧 FIX: Imperial slider values need conversion to metric for calculations
    // In Imperial mode, sliders store values in feet. Calculations expect meters.
    const isImperialMode = dynamicProject.data?.unit === unitEnum.Imperial;
    const toMetric = (valueInDisplayUnit: number): number => {
        return isImperialMode ? convert(valueInDisplayUnit).from('ft').to('m') : valueInDisplayUnit;
    };

    // Multi-layer geogrid detection with fallback chain (Paved-specific)
    // Layer 1: Form field | Layer 2: Geogrid array | Layer 3: Backend calculated values
    const hasBaseGeogrid = React.useMemo(() => {
        // Layer 1: Form field (primary source)
        const fromForm = Boolean(projectData?.baseCourse?.baseGeogrid && projectData.baseCourse.baseGeogrid !== 'none_selected');

        // Layer 2: Geogrid array (secondary source)
        const fromArray = Boolean(
            projectData?.geoGrid?.geogridBaseCourse?.geogrids &&
                projectData.geoGrid.geogridBaseCourse.geogrids.length > 0 &&
                projectData.geoGrid.geogridBaseCourse.geogrids[0]?.selectedGeogrid !== 'none_selected'
        );

        // Layer 3: Backend calculated values (tertiary fallback for Paved only)
        // If backend calculated different values, geogrid was used
        const stabilised = aashtoResults?.detailedCalculations?.softwareDesign?.base?.stabilised;
        const unstabilised = aashtoResults?.detailedCalculations?.softwareDesign?.base?.unstabilised;
        const fromValues = stabilised !== undefined && unstabilised !== undefined && stabilised !== unstabilised;

        if (fromForm) return true;
        if (fromArray) return true;
        return fromValues;
    }, [projectData?.baseCourse?.baseGeogrid, projectData?.geoGrid?.geogridBaseCourse?.geogrids, aashtoResults?.detailedCalculations?.softwareDesign?.base]);

    const hasSubbaseGeogrid = React.useMemo(() => {
        // Layer 1: Form field (primary source)
        const fromForm = Boolean(projectData?.subbaseCourse?.subbaseGeogrid && projectData.subbaseCourse.subbaseGeogrid !== 'none_selected');
        if (fromForm) return true;

        // Layer 2: Geogrid array (secondary source)
        const fromArray = Boolean(
            projectData?.geoGrid?.geogridSubBaseCourse?.geogrids &&
                projectData.geoGrid.geogridSubBaseCourse.geogrids.length > 0 &&
                projectData.geoGrid.geogridSubBaseCourse.geogrids[0]?.selectedGeogrid !== 'none_selected'
        );
        if (fromArray) return true;

        // Layer 3: Backend calculated values (tertiary fallback for Paved only)
        const stabilised = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.stabilised;
        const unstabilised = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.unstabilised;
        return stabilised !== undefined && unstabilised !== undefined && stabilised !== unstabilised;
    }, [projectData?.subbaseCourse?.subbaseGeogrid, projectData?.geoGrid?.geogridSubBaseCourse?.geogrids, aashtoResults?.detailedCalculations?.softwareDesign?.subbase]);

    // Surface Design inputs (for calculating surface thickness)
    // 🔧 FIX: Use FormValuesContext for real-time Canvas updates when slider changes
    // Convert to metric for calculations (Imperial sliders store feet, calculations expect meters)
    const surfaceDesignInputs = {
        baseThickness: toMetric(surfaceDesignBaseThicknessFromForm ?? 0.2),
        subbaseThickness: toMetric(surfaceDesignSubbaseThicknessFromForm ?? 0.4),
    };

    // Base Design inputs (for calculating base thickness)
    // 🔧 FIX: Use FormValuesContext + convert to metric for calculations
    const baseDesignInputs = {
        surfaceThickness: toMetric(baseDesignSurfaceThicknessFromForm ?? 0.1),
        subbaseThickness: toMetric(baseDesignSubbaseThicknessFromForm ?? 0.4),
    };

    // Subbase Design inputs (for calculating subbase thickness)
    // 🔧 FIX: Use FormValuesContext + convert to metric for calculations
    const subbaseDesignInputs = {
        surfaceThickness: toMetric(subbaseDesignSurfaceThicknessFromForm ?? 0.1),
        baseThickness: toMetric(subbaseDesignBaseThicknessFromForm ?? 0.2),
    }; // Removed useEffect that was overriding Excel default values with Goal Seek results
    // Focus Design should maintain the Excel default values (0.10, 0.20, 0.40) and allow user modifications

    const currentProjectType = dynamicProject.data?.type; // Get project type
    //get project method
    const currentProjectMethod = dynamicProject.data?.method; // Get project method
    logDebug('PavResults', 'Current project type:', currentProjectType, LogTag.FORM_SETUP);
    logDebug('PavResults', 'Current project method:', currentProjectMethod, LogTag.FORM_SETUP);

    // Create PavedCalculationService instance (replaces duplicate recalculateThickness function)
    const pavedCalculationService = useMemo(() => {
        if (!aashtoResults || !projectData) return null;
        return new PavedCalculationService(aashtoResults, projectData);
    }, [aashtoResults, projectData]);

    // Funzione per calcolare dinamicamente il numero di passaggi in base allo spessore
    const calculateDynamicPasses = (scenario: 'surface' | 'base' | 'subbase', inputs: any, stabilised = false) => {
        if (!aashtoResults?.detailedCalculations) return 0;

        // Calcola lo spessore totale per lo scenario corrente
        let totalThicknessCurrentScenario = 0;
        let totalThicknessOriginal = 0;

        if (scenario === 'surface') {
            // Surface Design: surface calcolato + base e subbase fissi
            const surfaceThickness = pavedCalculationService?.recalculateThickness('surface', inputs, stabilised) ?? 0;
            totalThicknessCurrentScenario = surfaceThickness + inputs.baseThickness + inputs.subbaseThickness;

            // Original: usa gli spessori del software design originale
            const softwareDesign = aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                totalThicknessOriginal = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
            }
        } else if (scenario === 'base') {
            // Base Design: base calcolato + surface e subbase fissi
            const baseThickness = pavedCalculationService?.recalculateThickness('base', inputs, stabilised) ?? 0;
            totalThicknessCurrentScenario = inputs.surfaceThickness + baseThickness + inputs.subbaseThickness;

            const softwareDesign = aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                totalThicknessOriginal = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
            }
        } else if (scenario === 'subbase') {
            // Subbase Design: subbase calcolato + surface e base fissi
            const subbaseThickness = pavedCalculationService?.recalculateThickness('subbase', inputs, stabilised) ?? 0;
            totalThicknessCurrentScenario = inputs.surfaceThickness + inputs.baseThickness + subbaseThickness;

            const softwareDesign = aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                totalThicknessOriginal = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
            }
        }

        // Il numero di passaggi base dal progetto
        const basePassesFirstYear = projectData?.trafficLoad?.esalFinalValue || aashtoResults.detailedCalculations.esalFactors.esalUnstabilised;

        // Calcola il rapporto di spessore totale
        const thicknessRatio = totalThicknessOriginal > 0 ? totalThicknessCurrentScenario / totalThicknessOriginal : 1;

        // Più spesso = più traffico possibile (rapporto > 1 = più traffico)
        // Più sottile = meno traffico possibile (rapporto < 1 = meno traffico)
        let dynamicPasses = basePassesFirstYear * thicknessRatio;

        // Per il caso stabilizzato, applica anche il TBR
        if (stabilised) {
            const tbr = aashtoResults.detailedCalculations.esalFactors.tbr;
            dynamicPasses *= tbr;
        }

        return Math.round(dynamicPasses);
    };

    // Funzione per calcolare il W18 dinamico in base ai passaggi
    const calculateDynamicW18 = (dynamicPasses: number) => {
        if (!aashtoResults?.detailedCalculations) return 0;

        // Calcola il W18 basato sui passaggi dinamici
        const originalPasses = projectData?.trafficLoad?.esalFinalValue || aashtoResults.detailedCalculations.esalFactors.esalUnstabilised;
        const originalW18 = aashtoResults.detailedCalculations.esalFactors.logW18Unstabilised;

        if (originalPasses === 0) return originalW18;

        // Calcola il rapporto e aggiusta il W18 di conseguenza
        const passesRatio = dynamicPasses / originalPasses;
        const dynamicW18 = originalW18 + Math.log10(passesRatio);

        return dynamicW18;
    };

    /**
     * Calcola e salva le thickness del design selezionato per il GeoGrid mapper.
     * Se più design sono abilitati, prende il primo nell'ordine: surface -> base -> subbase.
     * Le thickness calcolate vengono salvate in Redux per essere utilizzate dal GeoGridDataMapper.
     */
    const calculateAndSaveSelectedDesignThickness = useCallback(() => {
        if (!aashtoResults?.detailedCalculations || !projectData) {
            return;
        }

        // Determina quale design è selezionato (priorità: surface -> base -> subbase)
        let selectedDesignType: 'surface' | 'base' | 'subbase' | 'standard' = 'standard';
        let calculatedThickness = {
            surface: 0,
            base: 0,
            subbase: 0,
        };

        if (isSurfaceDesignEnabled) {
            // Surface Design: surface calcolato, base e subbase fissi
            selectedDesignType = 'surface';
            calculatedThickness = {
                surface: pavedCalculationService?.recalculateThickness('surface', surfaceDesignInputs, true) ?? 0, // Stabilised for geogrid
                base: surfaceDesignInputs.baseThickness,
                subbase: surfaceDesignInputs.subbaseThickness,
            };
        } else if (isBaseDesignEnabled) {
            // Base Design: base calcolato, surface e subbase fissi
            selectedDesignType = 'base';
            calculatedThickness = {
                surface: baseDesignInputs.surfaceThickness,
                base: pavedCalculationService?.recalculateThickness('base', baseDesignInputs, true) ?? 0, // Stabilised for geogrid
                subbase: baseDesignInputs.subbaseThickness,
            };
        } else if (isSubbaseDesignEnabled) {
            // Subbase Design: subbase calcolato, surface e base fissi
            selectedDesignType = 'subbase';
            calculatedThickness = {
                surface: subbaseDesignInputs.surfaceThickness,
                base: subbaseDesignInputs.baseThickness,
                subbase: pavedCalculationService?.recalculateThickness('subbase', subbaseDesignInputs, true) ?? 0, // Stabilised for geogrid
            };
        } else {
            // Nessun design abilitato, usa i valori standard dal software design
            selectedDesignType = 'standard';
            const softwareDesign = aashtoResults.detailedCalculations.softwareDesign;
            if (softwareDesign) {
                calculatedThickness = {
                    surface: softwareDesign.surface.stabilised,
                    base: softwareDesign.base.stabilised,
                    subbase: softwareDesign.subbase.stabilised,
                };
            }
        }

        // Controlla se i dati sono già aggiornati per evitare loop infiniti
        const existingCalculatedThickness = (projectData as any)?.calculatedDesignThickness;
        const isAlreadyCalculated =
            existingCalculatedThickness &&
            existingCalculatedThickness.selectedDesignType === selectedDesignType &&
            Math.abs(existingCalculatedThickness.thickness.surface - calculatedThickness.surface) < 0.001 &&
            Math.abs(existingCalculatedThickness.thickness.base - calculatedThickness.base) < 0.001 &&
            Math.abs(existingCalculatedThickness.thickness.subbase - calculatedThickness.subbase) < 0.001;

        // A NaN never matches the check above: publishing it would re-run this effect without end
        const isFiniteThickness = [calculatedThickness.surface, calculatedThickness.base, calculatedThickness.subbase].every(Number.isFinite);

        if (isAlreadyCalculated || !isFiniteThickness) {
            return;
        }

        // Salva i dati calcolati in Redux
        dispatch(
            setPavedCalculatedDesignThickness({
                selectedDesignType,
                thickness: calculatedThickness,
                isStabilised: true, // Per il GeoGrid usiamo sempre i valori stabilised
            })
        );
    }, [
        aashtoResults,
        pavedCalculationService,
        // Nota: escludiamo projectData dalle dipendenze per evitare loop infiniti quando si aggiorna calculatedDesignThickness
        isSurfaceDesignEnabled,
        isBaseDesignEnabled,
        isSubbaseDesignEnabled,
        surfaceDesignInputs,
        baseDesignInputs,
        subbaseDesignInputs,
        dispatch,
    ]);

    // Esegui il calcolo ogni volta che i dati AASHTO cambiano, ma non quando projectData cambia per evitare loop
    useEffect(() => {
        if (aashtoResults?.detailedCalculations && projectData) {
            calculateAndSaveSelectedDesignThickness();
        }
    }, [
        aashtoResults?.detailedCalculations,
        isSurfaceDesignEnabled,
        isBaseDesignEnabled,
        isSubbaseDesignEnabled,
        surfaceDesignInputs,
        baseDesignInputs,
        subbaseDesignInputs,
        calculateAndSaveSelectedDesignThickness,
    ]);

    const processedOutput = useMemo(() => {
        if (!rawOutput || !dynamicProject.data) return rawOutput;

        const projectUnit = dynamicProject.data.unit;
        if (projectUnit !== unitEnum.Imperial) {
            return rawOutput;
        }

        logDebug('PavResults', 'Project unit is Imperial. Converting API output to Imperial.', undefined, LogTag.UNIT_CONVERSION);
        const newOutput = JSON.parse(JSON.stringify(rawOutput)) as IDynamicProjectOutputData; // Deep clone

        if (newOutput.deckDesign) {
            const dd = newOutput.deckDesign;
            // Use imported convertFieldValue directly
            dd.unreinforcedHeight =
                convertFieldValue(
                    { name: 'deckDesign.unreinforcedHeight', type: 'number', unit: ['m', 'ft'], isConvertible: true },
                    dd.unreinforcedHeight,
                    unitEnum.SI,
                    unitEnum.Imperial,
                    'output.deckDesign.unreinforcedHeight'
                ) ?? dd.unreinforcedHeight;
            dd.reinforcedHeight =
                convertFieldValue(
                    { name: 'deckDesign.reinforcedHeight', type: 'number', unit: ['m', 'ft'], isConvertible: true },
                    dd.reinforcedHeight,
                    unitEnum.SI,
                    unitEnum.Imperial,
                    'output.deckDesign.reinforcedHeight'
                ) ?? dd.reinforcedHeight;
            dd.thicknessSaving =
                convertFieldValue(
                    { name: 'deckDesign.thicknessSaving', type: 'number', unit: ['m', 'ft'], isConvertible: true },
                    dd.thicknessSaving,
                    unitEnum.SI,
                    unitEnum.Imperial,
                    'output.deckDesign.thicknessSaving'
                ) ?? dd.thicknessSaving;

            if (dd.unreinforcedDeckAnalysisData) {
                dd.unreinforcedDeckAnalysisData.forEach((item, index) => {
                    item.h =
                        convertFieldValue(
                            { name: `deckDesign.unreinforcedDeckAnalysisData[${index}].h`, type: 'number', unit: ['m', 'ft'], isConvertible: true },
                            item.h,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.unreinforcedDeckAnalysisData[${index}].h`
                        ) ?? item.h;
                });
            }
            if (dd.reinforcedDeckAnalysisData) {
                dd.reinforcedDeckAnalysisData.forEach((item, index) => {
                    item.h =
                        convertFieldValue(
                            { name: `deckDesign.reinforcedDeckAnalysisData[${index}].h`, type: 'number', unit: ['m', 'ft'], isConvertible: true },
                            item.h,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.reinforcedDeckAnalysisData[${index}].h`
                        ) ?? item.h;
                });
            }
            if (dd.cbrLookupTable) {
                dd.cbrLookupTable.forEach((item, index) => {
                    item.cuKPa =
                        convertFieldValue(
                            { name: `deckDesign.cbrLookupTable[${index}].cuKPa`, type: 'number', unit: ['kPa', 'psi'], isConvertible: true },
                            item.cuKPa,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.cbrLookupTable[${index}].cuKPa`
                        ) ?? item.cuKPa;
                    item.hUnstabilisedM =
                        convertFieldValue(
                            { name: `deckDesign.cbrLookupTable[${index}].hUnstabilisedM`, type: 'number', unit: ['m', 'ft'], isConvertible: true },
                            item.hUnstabilisedM,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.cbrLookupTable[${index}].hUnstabilisedM`
                        ) ?? item.hUnstabilisedM;
                    item.hStabilisedM =
                        convertFieldValue(
                            { name: `deckDesign.cbrLookupTable[${index}].hStabilisedM`, type: 'number', unit: ['m', 'ft'], isConvertible: true },
                            item.hStabilisedM,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.cbrLookupTable[${index}].hStabilisedM`
                        ) ?? item.hStabilisedM;
                });
            }
            if ((dd as any).iterationTable && Array.isArray((dd as any).iterationTable)) {
                ((dd as any).iterationTable as any[]).forEach((item, index) => {
                    item.height =
                        convertFieldValue(
                            { name: `deckDesign.iterationTable[${index}].height`, type: 'number', unit: ['m', 'ft'], isConvertible: true },
                            item.height,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.iterationTable[${index}].height`
                        ) ?? item.height;
                    item.stabBearingCapacity =
                        convertFieldValue(
                            { name: `deckDesign.iterationTable[${index}].stabBearingCapacity`, type: 'number', unit: ['kPa', 'psi'], isConvertible: true },
                            item.stabBearingCapacity,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.iterationTable[${index}].stabBearingCapacity`
                        ) ?? item.stabBearingCapacity;
                    item.unstabBearingCapacity =
                        convertFieldValue(
                            { name: `deckDesign.iterationTable[${index}].unstabBearingCapacity`, type: 'number', unit: ['kPa', 'psi'], isConvertible: true },
                            item.unstabBearingCapacity,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.iterationTable[${index}].unstabBearingCapacity`
                        ) ?? item.unstabBearingCapacity;
                    item.verticalPressure =
                        convertFieldValue(
                            { name: `deckDesign.iterationTable[${index}].verticalPressure`, type: 'number', unit: ['kPa', 'psi'], isConvertible: true },
                            item.verticalPressure,
                            unitEnum.SI,
                            unitEnum.Imperial,
                            `output.deckDesign.iterationTable[${index}].verticalPressure`
                        ) ?? item.verticalPressure;
                });
            }
        }
        logDebug('PavResults', 'Converted output for Imperial display:', newOutput, LogTag.UNIT_CONVERSION);
        return newOutput;
    }, [rawOutput, dynamicProject.data?.unit, t]); // Added t to dependencies as it's used in mockFieldConfig indirectly if we were to translate field names (though not currently done here)

    logDebug('PavResults', 'Output for parking deck check:', processedOutput?.deckDesign, LogTag.CALCULATIONS);

    // Source inputs from dynamicProject.data.projectInfo.data
    const projectInputs = useMemo(() => {
        if (dynamicProject.data?.type === EProjectType.container_yard) {
            return dynamicProject.data.projectInfo?.data as IContainerYardData | undefined;
        } else if (dynamicProject.data?.type === EProjectType.parking_deck) {
            return dynamicProject.data.projectInfo?.data as IParkingDeckData | undefined;
        } else if (dynamicProject.data?.type === EProjectType.unpaved) {
            return dynamicProject.data.projectInfo?.data;
        }
        return undefined;
    }, [dynamicProject.data]);

    logDebug('PavResults', 'Output data:', processedOutput, LogTag.CALCULATIONS);
    logDebug('PavResults', 'Project inputs from dynamicProject:', projectInputs, LogTag.CALCULATIONS);

    // --- Prepara i dati per PAVED AASHTO - Diagramma UNSTABILIZED ---
    const pavedUnstabilizedGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.paved || !aashtoResults?.detailedCalculations?.softwareDesign || !dynamicProject.data) return null;
        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const { softwareDesign } = aashtoResults.detailedCalculations;

        // API returns values in METRIC. Convert to Imperial if needed.
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        const surfaceThickness = convertToDisplayUnit(softwareDesign.surface.unstabilised);
        const baseThickness = convertToDisplayUnit(softwareDesign.base.unstabilised);
        const subbaseThickness = convertToDisplayUnit(softwareDesign.subbase.unstabilised);
        const subgradeVisible = convertToDisplayUnit(0.5); // Visibile height for subgrade

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#D3D3D3' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            geogrid: undefined,
        };
    }, [dynamicProject, aashtoResults, currentProjectType, t]);

    // --- Prepara i dati per PAVED AASHTO - Diagramma STABILIZED ---
    const pavedStabilizedGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.paved || !aashtoResults?.detailedCalculations?.softwareDesign || !dynamicProject.data) return null;
        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const { softwareDesign } = aashtoResults.detailedCalculations;

        // API returns values in METRIC. Convert to Imperial if needed.
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        // Use unstabilised values if no geogrid selected, otherwise use stabilised
        // Note: Apply conversion AFTER selecting the right value (stabilised vs unstabilised)
        const surfaceThicknessRaw = softwareDesign.surface.stabilised;
        const baseThicknessRaw = hasBaseGeogrid ? softwareDesign.base.stabilised : softwareDesign.base.unstabilised;
        const subbaseThicknessRaw = hasSubbaseGeogrid ? softwareDesign.subbase.stabilised : softwareDesign.subbase.unstabilised;

        const surfaceThickness = convertToDisplayUnit(surfaceThicknessRaw);
        const baseThickness = convertToDisplayUnit(baseThicknessRaw);
        const subbaseThickness = convertToDisplayUnit(subbaseThicknessRaw);
        const subgradeVisible = convertToDisplayUnit(0.5); // Visibile height for subgrade

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#D3D3D3' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // For saving percentage, calculate using raw metric values (percentage is unit-independent)
        const unstabilizedTotalRaw = softwareDesign.surface.unstabilised + softwareDesign.base.unstabilised + softwareDesign.subbase.unstabilised;
        const stabilizedTotalRaw = surfaceThicknessRaw + baseThicknessRaw + subbaseThicknessRaw;
        const savingPercentage = unstabilizedTotalRaw > 0 ? ((unstabilizedTotalRaw - stabilizedTotalRaw) / unstabilizedTotalRaw) * 100 : 0;

        // Get geogrid information from project data
        // 🔧 FIX: Use projectData (already defined at line 62) instead of full nested path
        const firstBaseGeogrid = projectData?.geoGrid?.geogridBaseCourse?.geogrids?.[0];
        const geogridInfo: IGeogridInfo | undefined = firstBaseGeogrid
            ? {
                  positionFromBottomOfBase: convertToDisplayUnit(firstBaseGeogrid.position ?? 0),
                  label: firstBaseGeogrid.selectedGeogrid,
                  //   color: '#18c428',
              }
            : undefined;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            savingPercentage,
            unitLabel,
            geogrid: geogridInfo,
        };
    }, [dynamicProject, aashtoResults, currentProjectType, t, hasBaseGeogrid, hasSubbaseGeogrid]);

    // --- PAVED INPUTS TABLES (separate per accordion section) ---
    const { roadInfoTableData, trafficLoadTableData, surfaceTableData, baseCourseTableData, subbaseCourseTableData, subgradeTableData } = useMemo(() => {
        const roadInfoRows: string[][] = [];
        const trafficLoadRows: string[][] = [];
        const surfaceRows: string[][] = [];
        const baseCourseRows: string[][] = [];
        const subbaseCourseRows: string[][] = [];
        const subgradeRows: string[][] = [];

        if (currentProjectType !== EProjectType.paved || !projectData) {
            return {
                roadInfoTableData: roadInfoRows,
                trafficLoadTableData: trafficLoadRows,
                surfaceTableData: surfaceRows,
                baseCourseTableData: baseCourseRows,
                subbaseCourseTableData: subbaseCourseRows,
                subgradeTableData: subgradeRows,
            };
        }

        // FLAT structure: data is directly on projectData, not nested under method
        const methodData = projectData as any;
        const unitSystem = dynamicProject.data?.unit || 'Metric';

        // Helper function for formatting and converting length values (m → ft)
        // API and Redux store data in METRIC. For Imperial display, convert m → ft.
        const getConvertedValue = (value: unknown, convertLength = false): string => {
            if (value === null || value === undefined) return '';
            const numValue = typeof value === 'number' ? value : parseFloat(value as string);
            if (isNaN(numValue)) return '';

            // Convert meters to feet if Imperial mode and this is a length field
            if (convertLength && unitSystem === 'Imperial') {
                const convertedValue = convert(numValue).from('m').to('ft');
                return convertedValue.toFixed(3);
            }
            return numValue.toString();
        };

        const getUnit = (metricUnit: string, imperialUnit: string) => {
            return unitSystem === 'Metric' ? metricUnit : imperialUnit;
        };

        // Road Info section
        if (methodData.roadInfo) {
            const roadInfo = methodData.roadInfo;
            if (roadInfo.carriagewayWidth !== undefined) {
                roadInfoRows.push([t('width_of_carriageway_one_direction', 'Carriageway Width'), 'W', getConvertedValue(roadInfo.carriagewayWidth, true), getUnit('m', 'ft')]);
            }
            if (roadInfo.longitudinalSlope !== undefined) {
                roadInfoRows.push([t('longitudinal_slope', 'Longitudinal Slope'), 'S', roadInfo.longitudinalSlope.toString(), '%']);
            }
            if (roadInfo.transversalSlope !== undefined) {
                roadInfoRows.push([t('transversal_slope', 'Transversal Slope'), <span>S<sub>x</sub></span>, roadInfo.transversalSlope.toString(), '%']);
            }
        }

        // Traffic Load section
        if (methodData.trafficLoad) {
            const trafficLoad = methodData.trafficLoad;
            if (trafficLoad.functionalClassification) {
                trafficLoadRows.push([t('functional_classification', 'Functional Classification'), '-', trafficLoad.functionalClassification, '-']);
            }
            if (trafficLoad.roadType) {
                trafficLoadRows.push([t('road_type', 'Road Type'), '-', trafficLoad.roadType, '-']);
            }
            if (trafficLoad.reliabilityLevel !== undefined) {
                trafficLoadRows.push([t('reliability_level_r', 'Reliability Level'), 'R', trafficLoad.reliabilityLevel.toString(), '%']);
            }
            if (trafficLoad.combinedStandardError !== undefined) {
                trafficLoadRows.push([t('combined_standard_error_so', 'Combined Standard Error'), <span>S<sub>o</sub></span>, trafficLoad.combinedStandardError.toString(), '-']);
            }
            if (trafficLoad.initialServiceIndex !== undefined) {
                trafficLoadRows.push([t('initial_service_index_po', 'Initial Service Index'), <span>P<sub>o</sub></span>, trafficLoad.initialServiceIndex.toString(), '-']);
            }
            if (trafficLoad.terminalSurfaceIndex !== undefined) {
                trafficLoadRows.push([t('terminal_surface_index_pt', 'Terminal Surface Index'), <span>P<sub>t</sub></span>, trafficLoad.terminalSurfaceIndex.toString(), '-']);
            }
            if (trafficLoad.esalCalculationMode) {
                trafficLoadRows.push([t('esal_calculation_mode', 'ESAL Calculation Mode'), '-', trafficLoad.esalCalculationMode, '-']);
            }
            if (trafficLoad.esalFinalValue !== undefined) {
                trafficLoadRows.push([t('esal_final_value', 'ESAL Final Value'), 'ESAL', trafficLoad.esalFinalValue.toLocaleString('de-DE', { maximumFractionDigits: 0 }), '-']);
            }
        }

        // Surface section
        if (methodData.surface) {
            const surface = methodData.surface;
            if (surface.surfaceMaterial) {
                surfaceRows.push([t('select_surface_material', 'Surface Material'), '-', String(t(surface.surfaceMaterial, surface.surfaceMaterial)), '-']);
            }
            if (surface.minThickness !== undefined) {
                surfaceRows.push([t('minimum_thickness_of_surface_layer', 'Min Surface Thickness'), 't', getConvertedValue(surface.minThickness, true), getUnit('m', 'ft')]);
            }
        }

        // Base Course section
        const inputMethod = methodData.roadInfo?.subgradeInputMethod;
        if (methodData.baseCourse) {
            const baseCourse = methodData.baseCourse;
            if (baseCourse.baseMaterial) {
                baseCourseRows.push([t('select_base_material', 'Base Material'), '-', String(t(baseCourse.baseMaterial, baseCourse.baseMaterial)), '-']);
            }
            if (baseCourse.minThickness !== undefined) {
                baseCourseRows.push([t('minimum_thickness_of_base_layer', 'Min Base Thickness'), 't', getConvertedValue(baseCourse.minThickness, true), getUnit('m', 'ft')]);
            }
            if (inputMethod === 'cbr' && baseCourse.cbrBaseCourse !== undefined) {
                baseCourseRows.push([t('cbr_base_course', 'Base Course CBR'), 'CBR', baseCourse.cbrBaseCourse.toString(), '%']);
            }
            if (inputMethod === 'ev2' && baseCourse.ev2BaseCourse !== undefined) {
                baseCourseRows.push([t('ev2_base_course', 'Base Course Ev2'), <span>E<sub>v2</sub></span>, baseCourse.ev2BaseCourse.toString(), 'MPa']);
            }
            if (baseCourse.baseGeogrid && baseCourse.baseGeogrid.trim() !== '') {
                baseCourseRows.push([t('select_geogrid_for_base_course', 'Base Geogrid'), '-', String(t(baseCourse.baseGeogrid, baseCourse.baseGeogrid)), '-']);
            }
        }

        // Subbase Course section
        if (methodData.subbaseCourse) {
            const subbaseCourse = methodData.subbaseCourse;
            if (subbaseCourse.subbaseMaterial) {
                subbaseCourseRows.push([t('select_subbase_material', 'Subbase Material'), '-', String(t(subbaseCourse.subbaseMaterial, subbaseCourse.subbaseMaterial)), '-']);
            }
            if (subbaseCourse.minThickness !== undefined) {
                subbaseCourseRows.push([t('minimum_thickness_of_subbase_layer', 'Min Subbase Thickness'), 't', getConvertedValue(subbaseCourse.minThickness, true), getUnit('m', 'ft')]);
            }
            if (inputMethod === 'cbr' && subbaseCourse.cbrSubbaseCourse !== undefined) {
                subbaseCourseRows.push([t('cbr_subbase_course', 'Subbase Course CBR'), 'CBR', subbaseCourse.cbrSubbaseCourse.toString(), '%']);
            }
            if (inputMethod === 'ev2' && subbaseCourse.ev2SubbaseCourse !== undefined) {
                subbaseCourseRows.push([t('ev2_subbase_course', 'Subbase Course Ev2'), <span>E<sub>v2</sub></span>, subbaseCourse.ev2SubbaseCourse.toString(), 'MPa']);
            }
            if (subbaseCourse.subbaseGeogrid && subbaseCourse.subbaseGeogrid.trim() !== '') {
                subbaseCourseRows.push([t('select_geogrid_for_subbase_course', 'Subbase Geogrid'), '-', String(t(subbaseCourse.subbaseGeogrid, subbaseCourse.subbaseGeogrid)), '-']);
            }
        }

        // Subgrade section - only show the selected method based on toggle
        if (methodData.subgrade && inputMethod) {
            const subgrade = methodData.subgrade;
            if (inputMethod === 'cbr') {
                if (subgrade.cbrSubgrade !== undefined) {
                    subgradeRows.push([t('cbr_subgrade_label', 'CBR Subgrade'), 'CBR', subgrade.cbrSubgrade.toString(), '%']);
                }
            } else if (inputMethod === 'ev2') {
                if (subgrade.ev2Subgrade !== undefined) {
                    subgradeRows.push([t('ev2_subgrade_label', 'Ev2 Subgrade'), 'Ev2', subgrade.ev2Subgrade.toString(), 'MPa']);
                }
            }
        }

        return {
            roadInfoTableData: roadInfoRows,
            trafficLoadTableData: trafficLoadRows,
            surfaceTableData: surfaceRows,
            baseCourseTableData: baseCourseRows,
            subbaseCourseTableData: subbaseCourseRows,
            subgradeTableData: subgradeRows,
        };
    }, [currentProjectType, projectData, dynamicProject.data?.unit, t]);

    // Helper to check if there's any input data to display
    const hasAnyPavedInputData =
        roadInfoTableData.length > 0 ||
        trafficLoadTableData.length > 0 ||
        surfaceTableData.length > 0 ||
        baseCourseTableData.length > 0 ||
        subbaseCourseTableData.length > 0 ||
        subgradeTableData.length > 0;

    // --- Prepara i dati per PAVED AASHTO - Diagramma "INCREASED PASSAGES" con múltiple geogrids ---
    const pavedIncreasedPassagesGraphicData = useMemo(() => {
        if (currentProjectType !== EProjectType.paved || !aashtoResults?.detailedCalculations?.softwareDesign || !dynamicProject.data) return null;
        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const { softwareDesign } = aashtoResults.detailedCalculations;

        // Use unstabilised values if no geogrid selected, otherwise use stabilised
        const surfaceThickness = softwareDesign.surface.stabilised;
        const baseThickness = hasBaseGeogrid ? softwareDesign.base.stabilised : softwareDesign.base.unstabilised;
        const subbaseThickness = hasSubbaseGeogrid ? softwareDesign.subbase.stabilised : softwareDesign.subbase.unstabilised;
        const subgradeVisible = 0.5;

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#D3D3D3' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // 🔧 FIX: Use direct projectData access (FLAT structure) instead of method nesting
        // This matches the pattern used in hasBaseGeogrid detection at line 75
        const baseGeogridSelection = projectData?.baseCourse?.baseGeogrid;
        const subbaseGeogridSelection = projectData?.subbaseCourse?.subbaseGeogrid;

        logDebug(
            'pavedIncreasedPassagesGraphicData',
            'Form geogrid selections:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                baseCourse: projectData?.baseCourse,
            },
            LogTag.PAVED
        );

        // Use the same label logic as design sections for consistency
        const baseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.base?.geogridMaterialName
            || (baseGeogridSelection ? baseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');
        const subbaseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.geogridMaterialName
            || (subbaseGeogridSelection ? subbaseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');

        // Check if geogrid selections should be displayed
        const shouldShowBaseGeogrid = baseGeogridSelection && baseGeogridSelection !== 'none_selected' && !baseGeogridSelection.includes('none');

        const shouldShowSubbaseGeogrid = subbaseGeogridSelection && subbaseGeogridSelection !== 'none_selected' && !subbaseGeogridSelection.includes('none');

        logDebug(
            'pavedIncreasedPassagesGraphicData',
            'Geogrid display decisions:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                shouldShowBaseGeogrid,
                shouldShowSubbaseGeogrid,
            },
            LogTag.PAVED
        );

        // Conditionally create geogrid info for "Increased Passages" diagram using AASHTO results
        // Use the same label source as design sections for consistency
        const baseGeogridInfo: IGeogridInfo | undefined = shouldShowBaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness - 0.05, // Poco sopra la linea base-subbase
                  label: baseGeogridLabel, // Now uses AASHTO data directly like design sections
                  // color: '#18c428',
              }
            : undefined;

        // Create geogrid info for subbase geogrid (positioned near subbase-subgrade interface)
        const subbaseGeogridInfo: IGeogridInfo | undefined = shouldShowSubbaseGeogrid
            ? {
                  positionFromBottomOfBase: subbaseThickness - 0.05, // Poco sopra la linea subbase-subgrade (relativa al subbase)
                  label: subbaseGeogridLabel, // Now uses AASHTO data directly like design sections
                  color: '#ff6b35', // Different color for subbase geogrid
              }
            : undefined;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            // Conditionally include geogrid based on form selection
            geogrid: baseGeogridInfo,
            // Conditionally include subbase geogrid based on form selection
            subbaseGeogrid: subbaseGeogridInfo,
        };
    }, [dynamicProject, aashtoResults, currentProjectType, t, projectData, hasBaseGeogrid, hasSubbaseGeogrid]);

    // --- Prepara i dati per SURFACE DESIGN - Diagramma UNSTABILIZED ---
    const surfaceDesignUnstabilizedGraphicData = useMemo(() => {
        if (!isSurfaceDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // 🔧 FIX: Convert values from metric to Imperial if needed
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        // Calcola lo spessore surface usando recalculateThickness - result is in metric
        const surfaceThicknessRaw = pavedCalculationService?.recalculateThickness('surface', surfaceDesignInputs, false) ?? 0;
        const surfaceThickness = convertToDisplayUnit(surfaceThicknessRaw);

        // Usa gli spessori fissi dal form per base e subbase (converted for display)
        const baseThickness = convertToDisplayUnit(surfaceDesignInputs.baseThickness);
        const subbaseThickness = convertToDisplayUnit(surfaceDesignInputs.subbaseThickness);
        const subgradeVisible = convertToDisplayUnit(0.5);

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
        };
    }, [isSurfaceDesignEnabled, aashtoResults, dynamicProject.data, surfaceDesignInputs, t, pavedCalculationService]);

    // --- Prepara i dati per SURFACE DESIGN - Diagramma STABILIZED ---
    const surfaceDesignStabilizedGraphicData = useMemo(() => {
        if (!isSurfaceDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // 🔧 FIX: Convert values from metric to Imperial if needed
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        // Calcola lo spessore surface usando recalculateThickness (stabilizzato) - result is in metric
        const surfaceThicknessRaw = pavedCalculationService?.recalculateThickness('surface', surfaceDesignInputs, true) ?? 0;
        const surfaceThickness = convertToDisplayUnit(surfaceThicknessRaw);

        // Usa gli spessori fissi dal form per base e subbase (converted for display)
        const baseThickness = convertToDisplayUnit(surfaceDesignInputs.baseThickness);
        const subbaseThickness = convertToDisplayUnit(surfaceDesignInputs.subbaseThickness);
        const subgradeVisible = convertToDisplayUnit(0.5);

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // Calcola risparmio percentuale (using raw metric values - percentage is unit-independent)
        const unstabilizedSurfaceThicknessRaw = pavedCalculationService?.recalculateThickness('surface', surfaceDesignInputs, false) ?? 0;
        const savingPercentage = unstabilizedSurfaceThicknessRaw > 0 ? ((unstabilizedSurfaceThicknessRaw - surfaceThicknessRaw) / unstabilizedSurfaceThicknessRaw) * 100 : 0;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            savingPercentage: Math.max(0, savingPercentage),
            unitLabel,
        };
    }, [isSurfaceDesignEnabled, aashtoResults, dynamicProject.data, surfaceDesignInputs, t, pavedCalculationService]);

    // --- Prepara i dati per SURFACE DESIGN - Diagramma "INCREASED PASSAGES" con geogrids ---
    const surfaceDesignIncreasedPassagesGraphicData = useMemo(() => {
        if (!isSurfaceDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // 🔧 FIX: Convert values from metric to Imperial if needed
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        // Calcola lo spessore surface usando recalculateThickness (stabilizzato) - result is in metric
        const surfaceThicknessRaw = pavedCalculationService?.recalculateThickness('surface', surfaceDesignInputs, true) ?? 0;
        const surfaceThickness = convertToDisplayUnit(surfaceThicknessRaw);

        // Usa gli spessori fissi dal form per base e subbase (converted for display)
        const baseThickness = convertToDisplayUnit(surfaceDesignInputs.baseThickness);
        const subbaseThickness = convertToDisplayUnit(surfaceDesignInputs.subbaseThickness);
        const subgradeVisible = convertToDisplayUnit(0.5);

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // 🔧 FIX: Use direct FLAT access (consistent with fix at lines 735-736 for main results)
        const baseGeogridSelection = projectData?.baseCourse?.baseGeogrid;
        const subbaseGeogridSelection = projectData?.subbaseCourse?.subbaseGeogrid;

        const baseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.base?.geogridMaterialName
            || (baseGeogridSelection ? baseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');
        const subbaseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.geogridMaterialName
            || (subbaseGeogridSelection ? subbaseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');

        logDebug(
            'surfaceDesignIncreasedPassagesGraphicData',
            'Surface design form geogrid selections:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                isEnabled: isSurfaceDesignEnabled,
            },
            LogTag.PAVED
        );

        logDebug(
            'surfaceDesignIncreasedPassagesGraphicData',
            'Labels:',
            {
                baseGeogridLabel,
                subbaseGeogridLabel,
            },
            LogTag.CHARTS
        );

        // Check if geogrid selections should be displayed for surface design
        const shouldShowBaseGeogrid = baseGeogridSelection && baseGeogridSelection !== 'none_selected' && !baseGeogridSelection.includes('none');

        const shouldShowSubbaseGeogrid = subbaseGeogridSelection && subbaseGeogridSelection !== 'none_selected' && !subbaseGeogridSelection.includes('none');

        logDebug(
            'surfaceDesignIncreasedPassagesGraphicData',
            'Surface design geogrid display decisions:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                shouldShowBaseGeogrid,
                shouldShowSubbaseGeogrid,
            },
            LogTag.PAVED
        );

        // Conditionally create le informazioni per le geogrids (stessa logica dei diagrammi principali)
        const baseGeogridInfo: IGeogridInfo | undefined = shouldShowBaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness * 0.65,
                  label: baseGeogridLabel,
                  color: '#24244f',
              }
            : undefined;

        const subbaseGeogridInfo: IGeogridInfo | undefined = shouldShowSubbaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness + subbaseThickness * 0.65,
                  label: subbaseGeogridLabel,
                  color: '#ff6b35',
              }
            : undefined;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            // Aggiungo informazioni per la base geogrid
            geogrid: baseGeogridInfo,
            // Aggiungo informazioni per la subbase geogrid
            subbaseGeogrid: subbaseGeogridInfo,
        };
    }, [isSurfaceDesignEnabled, aashtoResults, dynamicProject.data, surfaceDesignInputs, t, pavedCalculationService, projectData]);

    // --- Prepara i dati per BASE DESIGN - Diagramma UNSTABILIZED ---
    const baseDesignUnstabilizedGraphicData = useMemo(() => {
        if (!isBaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // 🔧 FIX: Convert values from metric to Imperial if needed
        // Slider values are stored in METRIC, need to convert for display
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        // Usa gli spessori fissi dal form per surface e subbase (converted for display)
        const surfaceThickness = convertToDisplayUnit(baseDesignInputs.surfaceThickness);
        const subbaseThickness = convertToDisplayUnit(baseDesignInputs.subbaseThickness);
        // Calcola lo spessore base usando recalculateThickness (result is in metric, needs conversion)
        const baseThicknessRaw = pavedCalculationService?.recalculateThickness('base', baseDesignInputs, false) ?? 0;
        const baseThickness = convertToDisplayUnit(baseThicknessRaw);
        const subgradeVisible = convertToDisplayUnit(0.5);

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
        };
    }, [isBaseDesignEnabled, aashtoResults, dynamicProject.data, baseDesignInputs, t, pavedCalculationService]);

    // --- Prepara i dati per BASE DESIGN - Diagramma STABILIZED ---
    const baseDesignStabilizedGraphicData = useMemo(() => {
        if (!isBaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // 🔧 FIX: Convert values from metric to Imperial if needed
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        // Usa gli spessori fissi dal form per surface e subbase (converted for display)
        const surfaceThickness = convertToDisplayUnit(baseDesignInputs.surfaceThickness);
        const subbaseThickness = convertToDisplayUnit(baseDesignInputs.subbaseThickness);
        // Calcola lo spessore base usando recalculateThickness (stabilizzato) - result is in metric
        const baseThicknessRaw = pavedCalculationService?.recalculateThickness('base', baseDesignInputs, true) ?? 0;
        const baseThickness = convertToDisplayUnit(baseThicknessRaw);
        const subgradeVisible = convertToDisplayUnit(0.5);

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // Calcola risparmio percentuale (using raw metric values - percentage is unit-independent)
        const unstabilizedBaseThicknessRaw = pavedCalculationService?.recalculateThickness('base', baseDesignInputs, false) ?? 0;
        const savingPercentage = unstabilizedBaseThicknessRaw > 0 ? ((unstabilizedBaseThicknessRaw - baseThicknessRaw) / unstabilizedBaseThicknessRaw) * 100 : 0;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            savingPercentage: Math.max(0, savingPercentage),
            unitLabel,
        };
    }, [isBaseDesignEnabled, aashtoResults, dynamicProject.data, baseDesignInputs, t, pavedCalculationService]);

    // --- Prepara i dati per BASE DESIGN - Diagramma "INCREASED PASSAGES" con geogrids ---
    const baseDesignIncreasedPassagesGraphicData = useMemo(() => {
        if (!isBaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // 🔧 FIX: Convert values from metric to Imperial if needed
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        // Usa gli spessori fissi dal form per surface e subbase (converted for display)
        const surfaceThickness = convertToDisplayUnit(baseDesignInputs.surfaceThickness);
        const subbaseThickness = convertToDisplayUnit(baseDesignInputs.subbaseThickness);
        // Calcola lo spessore base usando recalculateThickness (stabilizzato) - result is in metric
        const baseThicknessRaw = pavedCalculationService?.recalculateThickness('base', baseDesignInputs, true) ?? 0;
        const baseThickness = convertToDisplayUnit(baseThicknessRaw);
        const subgradeVisible = convertToDisplayUnit(0.5);

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // 🔧 FIX: Use direct FLAT access (consistent with fix at lines 735-736 for main results)
        const baseGeogridSelection = projectData?.baseCourse?.baseGeogrid;
        const subbaseGeogridSelection = projectData?.subbaseCourse?.subbaseGeogrid;

        const baseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.base?.geogridMaterialName
            || (baseGeogridSelection ? baseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');
        const subbaseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.geogridMaterialName
            || (subbaseGeogridSelection ? subbaseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');

        logDebug(
            'baseDesignIncreasedPassagesGraphicData',
            'Base design form geogrid selections:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                isEnabled: isBaseDesignEnabled,
            },
            LogTag.PAVED
        );

        logDebug(
            'baseDesignIncreasedPassagesGraphicData',
            'Labels:',
            {
                baseGeogridLabel,
                subbaseGeogridLabel,
            },
            LogTag.CHARTS
        );

        // Check if geogrid selections should be displayed for base design
        const shouldShowBaseGeogrid = baseGeogridSelection && baseGeogridSelection !== 'none_selected' && !baseGeogridSelection.includes('none');

        const shouldShowSubbaseGeogrid = subbaseGeogridSelection && subbaseGeogridSelection !== 'none_selected' && !subbaseGeogridSelection.includes('none');

        logDebug(
            'baseDesignIncreasedPassagesGraphicData',
            'Base design geogrid display decisions:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                shouldShowBaseGeogrid,
                shouldShowSubbaseGeogrid,
            },
            LogTag.PAVED
        );

        // Conditionally create le informazioni per le geogrids (stessa logica dei diagrammi principali)
        const baseGeogridInfo: IGeogridInfo | undefined = shouldShowBaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness * 0.65,
                  label: baseGeogridLabel,
                  color: '#24244f',
              }
            : undefined;

        const subbaseGeogridInfo: IGeogridInfo | undefined = shouldShowSubbaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness + subbaseThickness * 0.65,
                  label: subbaseGeogridLabel,
                  color: '#ff6b35',
              }
            : undefined;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            // Aggiungo informazioni per la base geogrid
            geogrid: baseGeogridInfo,
            // Aggiungo informazioni per la subbase geogrid
            subbaseGeogrid: subbaseGeogridInfo,
        };
    }, [isBaseDesignEnabled, aashtoResults, dynamicProject.data, baseDesignInputs, t, pavedCalculationService, projectData]);

    // --- Prepara i dati per SUBBASE DESIGN - Diagramma UNSTABILIZED ---
    const subbaseDesignUnstabilizedGraphicData = useMemo(() => {
        if (!isSubbaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // 🔧 FIX: Convert values from metric to Imperial if needed
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        // Usa gli spessori fissi dal form per surface e base (converted for display)
        const surfaceThickness = convertToDisplayUnit(subbaseDesignInputs.surfaceThickness);
        const baseThickness = convertToDisplayUnit(subbaseDesignInputs.baseThickness);
        // Calcola lo spessore subbase usando recalculateThickness - result is in metric
        const subbaseThicknessRaw = pavedCalculationService?.recalculateThickness('subbase', subbaseDesignInputs, false) ?? 0;
        const subbaseThickness = convertToDisplayUnit(subbaseThicknessRaw);
        const subgradeVisible = convertToDisplayUnit(0.5);

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
        };
    }, [isSubbaseDesignEnabled, aashtoResults, dynamicProject.data, subbaseDesignInputs, t, pavedCalculationService]);

    // --- Prepara i dati per SUBBASE DESIGN - Diagramma STABILIZED ---
    const subbaseDesignStabilizedGraphicData = useMemo(() => {
        if (!isSubbaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // 🔧 FIX: Convert values from metric to Imperial if needed
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        // Usa gli spessori fissi dal form per surface e base (converted for display)
        const surfaceThickness = convertToDisplayUnit(subbaseDesignInputs.surfaceThickness);
        const baseThickness = convertToDisplayUnit(subbaseDesignInputs.baseThickness);
        // Calcola lo spessore subbase usando recalculateThickness (stabilizzato) - result is in metric
        const subbaseThicknessRaw = pavedCalculationService?.recalculateThickness('subbase', subbaseDesignInputs, true) ?? 0;
        const subbaseThickness = convertToDisplayUnit(subbaseThicknessRaw);
        const subgradeVisible = convertToDisplayUnit(0.5);

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // Calcola risparmio percentuale (using raw metric values - percentage is unit-independent)
        const unstabilizedSubbaseThicknessRaw = pavedCalculationService?.recalculateThickness('subbase', subbaseDesignInputs, false) ?? 0;
        const savingPercentage = unstabilizedSubbaseThicknessRaw > 0 ? ((unstabilizedSubbaseThicknessRaw - subbaseThicknessRaw) / unstabilizedSubbaseThicknessRaw) * 100 : 0;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            savingPercentage: Math.max(0, savingPercentage),
            unitLabel,
        };
    }, [isSubbaseDesignEnabled, aashtoResults, dynamicProject.data, subbaseDesignInputs, t, pavedCalculationService]);

    // --- Prepara i dati per SUBBASE DESIGN - Diagramma "INCREASED PASSAGES" con geogrids ---
    const subbaseDesignIncreasedPassagesGraphicData = useMemo(() => {
        if (!isSubbaseDesignEnabled || !aashtoResults?.detailedCalculations || !dynamicProject.data) return null;

        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const unitLabel = isImperial ? t('ft_unit', 'ft') : t('m_unit', 'm');

        // 🔧 FIX: Convert values from metric to Imperial if needed
        const convertToDisplayUnit = (valueInMeters: number): number => {
            return isImperial ? convert(valueInMeters).from('m').to('ft') : valueInMeters;
        };

        // Usa gli spessori fissi dal form per surface e base (converted for display)
        const surfaceThickness = convertToDisplayUnit(subbaseDesignInputs.surfaceThickness);
        const baseThickness = convertToDisplayUnit(subbaseDesignInputs.baseThickness);
        // Calcola lo spessore subbase usando recalculateThickness (stabilizzato) - result is in metric
        const subbaseThicknessRaw = pavedCalculationService?.recalculateThickness('subbase', subbaseDesignInputs, true) ?? 0;
        const subbaseThickness = convertToDisplayUnit(subbaseThicknessRaw);
        const subgradeVisible = convertToDisplayUnit(0.5);

        const surfaceLayer: ILayerData = { name: t('surface_layer', 'Surface'), thickness: surfaceThickness, color: '#2C2C2C' };
        const baseLayer: ILayerData = { name: t('base_layer', 'Base'), thickness: baseThickness, color: '#8B7355' };
        const subbaseLayer: ILayerData = { name: t('subbase_layer', 'Subbase'), thickness: subbaseThickness, color: '#F5DEB3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        const totalThickness = surfaceThickness + baseThickness + subbaseThickness;

        // 🔧 FIX: Use direct FLAT access (consistent with fix at lines 735-736 for main results)
        const baseGeogridSelection = projectData?.baseCourse?.baseGeogrid;
        const subbaseGeogridSelection = projectData?.subbaseCourse?.subbaseGeogrid;

        const baseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.base?.geogridMaterialName
            || (baseGeogridSelection ? baseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');
        const subbaseGeogridLabel = aashtoResults?.detailedCalculations?.softwareDesign?.subbase?.geogridMaterialName
            || (subbaseGeogridSelection ? subbaseGeogridSelection.replace(/_/g, ' ').toUpperCase() : '');

        logDebug(
            'subbaseDesignIncreasedPassagesGraphicData',
            'Subbase design form geogrid selections:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                isEnabled: isSubbaseDesignEnabled,
            },
            LogTag.PAVED
        );

        logDebug(
            'subbaseDesignIncreasedPassagesGraphicData',
            'Labels:',
            {
                baseGeogridLabel,
                subbaseGeogridLabel,
            },
            LogTag.CHARTS
        );

        // Check if geogrid selections should be displayed for subbase design
        const shouldShowBaseGeogrid = baseGeogridSelection && baseGeogridSelection !== 'none_selected' && !baseGeogridSelection.includes('none');

        const shouldShowSubbaseGeogrid = subbaseGeogridSelection && subbaseGeogridSelection !== 'none_selected' && !subbaseGeogridSelection.includes('none');

        logDebug(
            'subbaseDesignIncreasedPassagesGraphicData',
            'Subbase design geogrid display decisions:',
            {
                baseGeogridSelection,
                subbaseGeogridSelection,
                shouldShowBaseGeogrid,
                shouldShowSubbaseGeogrid,
            },
            LogTag.PAVED
        );

        // Conditionally create le informazioni per le geogrids (stessa logica dei diagrammi principali)
        const baseGeogridInfo: IGeogridInfo | undefined = shouldShowBaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness * 0.65,
                  label: baseGeogridLabel,
                  color: '#24244f',
              }
            : undefined;

        const subbaseGeogridInfo: IGeogridInfo | undefined = shouldShowSubbaseGeogrid
            ? {
                  positionFromBottomOfBase: baseThickness + subbaseThickness * 0.65,
                  label: subbaseGeogridLabel,
                  color: '#ff6b35',
              }
            : undefined;

        return {
            surfaceLayer,
            baseLayer,
            subbaseLayer,
            subgrade,
            rightDimensionValue: totalThickness,
            unitLabel,
            // Aggiungo informazioni per la base geogrid
            geogrid: baseGeogridInfo,
            // Aggiungo informazioni per la subbase geogrid
            subbaseGeogrid: subbaseGeogridInfo,
        };
    }, [isSubbaseDesignEnabled, aashtoResults, dynamicProject.data, subbaseDesignInputs, t, pavedCalculationService, projectData]);

    // Canvas capture functions for PDF export
    const waitForElements = useCallback((selectors: string[], timeout = 5000): Promise<HTMLCanvasElement[]> => {
        return new Promise(resolve => {
            const startTime = Date.now();
            const check = () => {
                const elements = selectors
                    .map(selector => {
                        const element = document.getElementById(selector) as HTMLCanvasElement;
                        // More lenient check - just verify element exists and is a canvas
                        if (element && element.tagName === 'CANVAS') {
                            return element;
                        }
                        return null;
                    })
                    .filter(Boolean) as HTMLCanvasElement[];

                logDebug('Results_Paved', `Canvas check: Found ${elements.length}/${selectors.length} canvas elements`, undefined, LogTag.PDF_IMAGES);

                if (elements.length >= 1) {
                    // Accept if we have at least 1 canvas, wait a bit more for final rendering
                    setTimeout(() => resolve(elements), 500);
                } else if (Date.now() - startTime > timeout) {
                    logError('Results_Paved', `Timeout waiting for canvas elements. Found ${elements.length}/${selectors.length} elements`, undefined, LogTag.PDF_IMAGES);
                    // Log which elements were found and which weren't
                    selectors.forEach(selector => {
                        const element = document.getElementById(selector);
                        if (element) {
                            logDebug(
                                'Results_Paved',
                                `Canvas ${selector}: found, tagName=${element.tagName}, width=${element.clientWidth}, height=${element.clientHeight}`,
                                undefined,
                                LogTag.PDF_IMAGES
                            );
                        } else {
                            logError('Results_Paved', `Canvas ${selector}: not found in DOM`, undefined, LogTag.PDF_IMAGES);
                        }
                    });
                    resolve(elements); // Return what we have
                } else {
                    setTimeout(check, 100);
                }
            };
            check();
        });
    }, []);

    const captureSimplePavedImages = useCallback(async () => {
        logDebug('Results_Paved', 'Starting simple direct canvas capture...', undefined, LogTag.PDF_IMAGES);

        // Simple direct approach - try to find canvas elements by ID
        const capturedImages: Record<string, string> = {};

        // Canvas ID to PDF property mapping
        const canvasMapping = [
            { id: 'tire-layer-unstabilized', pdfProperty: 'tireImageUnreinforced' },
            { id: 'tire-layer-increased-passages', pdfProperty: 'tireImageReinforced' },
            { id: 'tire-layer-geogrid', pdfProperty: 'tireImageGeogrid' },
        ];

        for (const mapping of canvasMapping) {
            const canvas = document.getElementById(mapping.id) as HTMLCanvasElement;

            if (canvas) {
                logDebug('Results_Paved', `Found canvas: ${mapping.id} (${canvas.tagName}, ${canvas.width}x${canvas.height})`, undefined, LogTag.PDF_IMAGES);

                try {
                    const dataUrl = await html2canvas(canvas, {
                        backgroundColor: null,
                        scale: 2,
                        logging: false,
                        useCORS: true,
                    }).then(capturedCanvas => capturedCanvas.toDataURL('image/png'));

                    capturedImages[mapping.pdfProperty] = dataUrl;
                    logDebug('Results_Paved', `Successfully captured: ${mapping.id} -> ${mapping.pdfProperty}`, undefined, LogTag.PDF_IMAGES);
                } catch (error) {
                    logError('Results_Paved', `Failed to capture ${mapping.id}:`, error, LogTag.PDF_IMAGES);
                }
            } else {
                logError('Results_Paved', `Canvas not found: ${mapping.id}`, undefined, LogTag.PDF_IMAGES);
            }
        }

        // Update state with captured images
        setCapturedCanvasImages(capturedImages);
        logDebug('Results_Paved', `Simple canvas capture completed. Captured ${Object.keys(capturedImages).length} images:`, Object.keys(capturedImages));
    }, []);

    const capturePavedImages = useCallback(async () => {
        if (!aashtoResults?.detailedCalculations) {
            logDebug('Results_Paved', 'No AASHTO results available for image capture', undefined, LogTag.PDF_IMAGES);
            return;
        }

        try {
            // Focus only on the 3 tire layer canvas elements that actually exist
            const tireCanvasIds = ['tire-layer-unstabilized', 'tire-layer-increased-passages', 'tire-layer-geogrid'];

            logDebug('Results_Paved', 'Starting canvas capture for paved tire images...', undefined, LogTag.PDF_IMAGES);

            // Wait for canvas elements to be available
            const canvasElements = await waitForElements(tireCanvasIds);

            if (canvasElements.length === 0) {
                logError('Results_Paved', 'No canvas elements found for capture', undefined, LogTag.PDF_IMAGES);

                // Fallback: try direct element search without waiting
                logDebug('Results_Paved', 'Attempting fallback search for canvas elements...', undefined, LogTag.PDF_IMAGES);
                const fallbackElements = tireCanvasIds.map(id => document.getElementById(id)).filter(Boolean) as HTMLCanvasElement[];

                if (fallbackElements.length > 0) {
                    logDebug(
                        'Results_Paved',
                        `Fallback found ${fallbackElements.length} elements:`,
                        fallbackElements.map(e => e.id),
                        LogTag.PDF_IMAGES
                    );
                    // Use fallback elements
                    canvasElements.push(...fallbackElements);
                } else {
                    logError('Results_Paved', 'Fallback search also found no elements', undefined, LogTag.PDF_IMAGES);
                    return;
                }
            }

            logDebug(
                'Results_Paved',
                `Found ${canvasElements.length} canvas elements for capture:`,
                canvasElements.map(c => c.id),
                LogTag.PDF_IMAGES
            );

            // Capture images from available canvas elements
            const capturedImages: { [key: string]: string } = {};

            // Define mapping from canvas IDs to PDF property names
            const canvasIdToPdfPropertyMap: { [key: string]: string } = {
                'tire-layer-unstabilized': 'tireImageUnreinforced',
                'tire-layer-increased-passages': 'tireImageReinforced',
                'tire-layer-geogrid': 'tireImageGeogrid',
                'surface-design-unstabilized': 'surfaceImageUnreinforced',
                'surface-design-stabilized': 'surfaceImageReinforced',
                'surface-design-increased-passages': 'surfaceImageReinforced',
                'base-design-unstabilized': 'baseImageUnreinforced',
                'base-design-stabilized': 'baseImageReinforced',
                'base-design-increased-passages': 'baseImageReinforced',
                'subbase-design-unstabilized': 'subbaseImageUnreinforced',
                'subbase-design-stabilized': 'subbaseImageReinforced',
                'subbase-design-increased-passages': 'subbaseImageReinforced',
                'chart-image': 'chartImage',
                'project-map': 'projectMapImage',
                'geogrid-image': 'geogridImage',
            };

            for (const canvas of canvasElements) {
                if (canvas && canvas.id) {
                    logDebug('Results_Paved', `Attempting to capture canvas: ${canvas.id} (${canvas.width}x${canvas.height})`, undefined, LogTag.PDF_IMAGES);
                    try {
                        const dataUrl = await html2canvas(canvas, {
                            backgroundColor: null,
                            scale: 2,
                            logging: false,
                            useCORS: true,
                        }).then(capturedCanvas => capturedCanvas.toDataURL('image/png'));

                        // Map canvas ID to PDF property name
                        const pdfPropertyName = canvasIdToPdfPropertyMap[canvas.id] || canvas.id;
                        capturedImages[pdfPropertyName] = dataUrl;
                        logDebug('Results_Paved', `Successfully captured image for: ${canvas.id} -> ${pdfPropertyName}`, undefined, LogTag.PDF_IMAGES);
                    } catch (error) {
                        logError('Results_Paved', `Failed to capture image for ${canvas.id}:`, error, LogTag.PDF_IMAGES);
                    }
                } else {
                    logError('Results_Paved', 'Canvas element has no ID:', canvas, LogTag.PDF_IMAGES);
                }
            }

            // Update state with captured images
            setCapturedCanvasImages(capturedImages);
            logDebug('Results_Paved', `Canvas capture completed. Captured ${Object.keys(capturedImages).length} images`, undefined, LogTag.PDF_IMAGES);
        } catch (error) {
            logError('Results_Paved', 'Error during canvas capture:', error, LogTag.PDF_IMAGES);
        }
    }, [aashtoResults, waitForElements]);

    // Trigger canvas capture when results are available
    useEffect(() => {
        if (aashtoResults?.detailedCalculations && currentProjectType === EProjectType.paved) {
            // Use simple direct approach with shorter delay
            const timer = setTimeout(() => {
                captureSimplePavedImages();
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [aashtoResults, currentProjectType, captureSimplePavedImages]);

    // This function renders the new AASHTO results from the Redux store.
    const renderPavedResults = () => {
        if (currentProjectType !== EProjectType.paved || !aashtoResults) {
            return null;
        }

        const { totalSN, finalSN, verification } = aashtoResults;

        return (
            <Paper sx={{ p: 3, mt: 2, mb: 3, backgroundColor: '#fafafa' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    AASHTO &rsquo;93 Pavement Design Results
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Overall Structural Number (SN)
                            </Typography>
                            <Typography variant="body1">
                                <strong>Total Required SN:</strong> {totalSN.toFixed(3)} inches
                            </Typography>
                            <Typography variant="body1">
                                <strong>Final Provided SN:</strong> {finalSN.toFixed(3)} inches
                            </Typography>
                            <Typography variant="body2" color={verification.isAdequate ? 'success.main' : 'error.main'} sx={{ mt: 1 }}>
                                {verification.message}
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                {/* Detailed Excel Calculations Table */}
                {aashtoResults.detailedCalculations && (
                    <Paper sx={{ p: 3, mt: 2, backgroundColor: '#f5f5f5' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Detailed AASHTO Calculations (Excel Format)
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Basic Parameters */}
                            <Grid item xs={12} md={4}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Basic AASHTO Parameters
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>ΔPSI = po - pt</strong>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {aashtoResults?.detailedCalculations?.basicParameters?.dpsi !== undefined
                                                        ? aashtoResults.detailedCalculations.basicParameters.dpsi.toFixed(1)
                                                        : 'N/A (dpsi undefined)'}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Standard normal deviate ZR</strong>
                                                </TableCell>
                                                <TableCell align="right">{aashtoResults.detailedCalculations.basicParameters.zr.toFixed(3)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Structural Number SN</strong>
                                                </TableCell>
                                                <TableCell align="right">{aashtoResults.detailedCalculations.basicParameters.structuralNumberSN.toFixed(3)}</TableCell>
                                                <TableCell>inches</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell></TableCell>
                                                <TableCell align="right">{(aashtoResults.detailedCalculations.basicParameters.structuralNumberSN * 0.0254).toFixed(3)}</TableCell>
                                                <TableCell>m</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Mr - kPa</strong>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <strong>Mr - psi</strong>
                                                </TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Subgrade</TableCell>
                                                <TableCell align="right">{aashtoResults.detailedCalculations.basicParameters.mrSubgradeKpa.toFixed(0)}</TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.basicParameters.mrSubgradePsi.toFixed(0)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>

                            {/* Layer Coefficients */}
                            <Grid item xs={12} md={8}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Layer Parameters
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Layer</strong>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <strong>a</strong>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <strong>Unstabilised m</strong>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <strong>Stabilised m</strong>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Surface layer 1</strong>
                                                </TableCell>
                                                <TableCell align="center">{aashtoResults.detailedCalculations.layerCoefficients.surface.a1.toFixed(2)}</TableCell>
                                                <TableCell align="center">1.00</TableCell>
                                                <TableCell align="center">1.00</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Base course 2</strong>
                                                </TableCell>
                                                <TableCell align="center">{aashtoResults.detailedCalculations.layerCoefficients.base.a2.toFixed(2)}</TableCell>
                                                <TableCell align="center">{aashtoResults.detailedCalculations.layerCoefficients.base.m2.toFixed(2)}</TableCell>
                                                <TableCell align="center">
                                                    {(aashtoResults.detailedCalculations.layerCoefficients.base.m2_stabilised || aashtoResults.detailedCalculations.layerCoefficients.base.m2).toFixed(
                                                        2
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Subbase 3</strong>
                                                </TableCell>
                                                <TableCell align="center">{(Math.ceil(aashtoResults.detailedCalculations.layerCoefficients.subbase.a3 * 100) / 100).toFixed(2)}</TableCell>
                                                <TableCell align="center">{aashtoResults.detailedCalculations.layerCoefficients.subbase.m3.toFixed(2)}</TableCell>
                                                <TableCell align="center">
                                                    {(
                                                        aashtoResults.detailedCalculations.layerCoefficients.subbase.m3_stabilised || aashtoResults.detailedCalculations.layerCoefficients.subbase.m3
                                                    ).toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>

                                    {/* LCR Values */}
                                    {(aashtoResults.detailedCalculations.layerCoefficients.base.lcr || aashtoResults.detailedCalculations.layerCoefficients.subbase.lcr) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                <strong>LCR Values</strong>
                                            </Typography>
                                            <Table size="small">
                                                <TableBody>
                                                    {aashtoResults.detailedCalculations.layerCoefficients.base.lcr && (
                                                        <TableRow>
                                                            <TableCell>
                                                                <strong>Base Stabilised LCR</strong>
                                                            </TableCell>
                                                            <TableCell>{aashtoResults.detailedCalculations.layerCoefficients.base.lcr.toFixed(6)}</TableCell>
                                                        </TableRow>
                                                    )}
                                                    {aashtoResults.detailedCalculations.layerCoefficients.subbase.lcr && (
                                                        <TableRow>
                                                            <TableCell>
                                                                <strong>SubBase Stabilised LCR</strong>
                                                            </TableCell>
                                                            <TableCell>{aashtoResults.detailedCalculations.layerCoefficients.subbase.lcr.toFixed(6)}</TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    )}
                                </Card>
                            </Grid>

                            {/* ESAL Factors */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        ESAL & Calculation Factors
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>f1 (|ZR| × So)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.f1.toFixed(6)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>f2 (log term)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.f2.toFixed(6)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>f3 (Mr term)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.f3.toFixed(6)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>LogW18 (Unstabilised)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.logW18Unstabilised.toFixed(6)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>LogW18 (Stabilised)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.logW18Stabilised.toFixed(6)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>ESAL (Unstabilised)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.esalUnstabilised.toExponential(3)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>ESAL (Stabilised)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.esalStabilised.toExponential(3)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>TBR (Traffic Benefit Ratio)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.esalFactors.tbr.toFixed(2)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>

                            {/* Structural Numbers (SN) */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Structural Numbers (SN)
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>SN Unstabilised</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.snCalculations.snUnstabilised.toFixed(2)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>SN Stabilised</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.snCalculations.snStabilised.toFixed(2)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>SN Requested</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.totalSN.toFixed(2)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>SN1&rsquo; (Surface)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.snCalculations.sn1Prime.toFixed(2)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>SN2&rsquo; (Base)</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.snCalculations.sn2Prime.toFixed(2)}</TableCell>
                                            </TableRow>
                                            {aashtoResults.detailedCalculations.snCalculations.sn3Prime && (
                                                <TableRow>
                                                    <TableCell>
                                                        <strong>SN3&rsquo; (Subbase)</strong>
                                                    </TableCell>
                                                    <TableCell>{aashtoResults.detailedCalculations.snCalculations.sn3Prime.toFixed(2)}</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>

                            {/* Design Thickness Breakdown */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Design Layer Thickness (meters)
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Layer</strong>
                                                </TableCell>
                                                <TableCell>
                                                    <strong>Unstabilised</strong>
                                                </TableCell>
                                                <TableCell>
                                                    <strong>Stabilised</strong>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Surface</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.surface.unstabilised.toFixed(3) || 'N/A'}</TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.surface.stabilised.toFixed(3) || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Base</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.base.unstabilised.toFixed(3) || 'N/A'}</TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.base.stabilised.toFixed(3) || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <strong>Subbase</strong>
                                                </TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.subbase.unstabilised.toFixed(3) || 'N/A'}</TableCell>
                                                <TableCell>{aashtoResults.detailedCalculations.softwareDesign?.subbase.stabilised.toFixed(3) || 'N/A'}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>
                        </Grid>
                    </Paper>
                )}

                {/* Design_LCR Final Thickness Comparison */}
                {aashtoResults.detailedCalculations?.designLCRThickness && (
                    <Paper sx={{ p: 3, mt: 2, backgroundColor: '#e8f5e8' }}>
                        <Grid container spacing={3}>
                            {/* Show message if no design analysis is enabled */}
                            {!isSurfaceDesignEnabled && !isBaseDesignEnabled && !isSubbaseDesignEnabled && (
                                <Grid item xs={12}>
                                    <Card sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                                        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                                            📋 No Design Analysis Enabled
                                        </Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            Enable one or more design analysis options in the form to see thickness comparison tables:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            • Surface Design Analysis
                                            <br />
                                            • Base Design Analysis
                                            <br />• Subbase Design Analysis
                                        </Typography>
                                    </Card>
                                </Grid>
                            )}

                            {/* Surface Design Focus */}
                            {isSurfaceDesignEnabled && (
                                <Grid item xs={12} md={4}>
                                    <Card sx={{ p: 2 }}>
                                        {/* Excel-style Design SURFACE layer thickness table */}
                                        <Table size="small" sx={{ border: '2px solid #333', '& .MuiTableCell-root': { border: '1px solid #666' } }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e0e0e0' }} colSpan={5}>
                                                        Design SURFACE layer thickness
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Unstabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Stabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Surface</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>
                                                        {(pavedCalculationService?.recalculateThickness('surface', surfaceDesignInputs, false) ?? 0).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>
                                                        {(pavedCalculationService?.recalculateThickness('surface', surfaceDesignInputs, true) ?? 0).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Base</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{surfaceDesignInputs.baseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{surfaceDesignInputs.baseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Subbase</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{surfaceDesignInputs.subbaseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{surfaceDesignInputs.subbaseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Card>
                                </Grid>
                            )}

                            {/* Base Design Focus */}
                            {isBaseDesignEnabled && (
                                <Grid item xs={12} md={4}>
                                    <Card sx={{ p: 2 }}>
                                        {/* Excel-style Design BASE layer thickness table */}
                                        <Table size="small" sx={{ border: '2px solid #333', '& .MuiTableCell-root': { border: '1px solid #666' } }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e0e0e0' }} colSpan={5}>
                                                        Design BASE layer thickness
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Unstabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Stabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Surface</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{baseDesignInputs.surfaceThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{baseDesignInputs.surfaceThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Base</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>
                                                        {(pavedCalculationService?.recalculateThickness('base', baseDesignInputs, false) ?? 0).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>
                                                        {(pavedCalculationService?.recalculateThickness('base', baseDesignInputs, true) ?? 0).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Subbase</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{baseDesignInputs.subbaseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{baseDesignInputs.subbaseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Card>
                                </Grid>
                            )}

                            {/* Subbase Design Focus */}
                            {isSubbaseDesignEnabled && (
                                <Grid item xs={12} md={4}>
                                    <Card sx={{ p: 2 }}>
                                        {/* Excel-style Design SUBBASE layer thickness table */}
                                        <Table size="small" sx={{ border: '2px solid #333', '& .MuiTableCell-root': { border: '1px solid #666' } }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e0e0e0' }} colSpan={5}>
                                                        Design SUBBASE layer thickness
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Unstabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#ffff99' }}>Stabilised</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Surface</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{subbaseDesignInputs.surfaceThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{subbaseDesignInputs.surfaceThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Base</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{subbaseDesignInputs.baseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{subbaseDesignInputs.baseThickness.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Subbase</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>
                                                        {(pavedCalculationService?.recalculateThickness('subbase', subbaseDesignInputs, false) ?? 0).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', backgroundColor: '#ffff99' }}>
                                                        {(pavedCalculationService?.recalculateThickness('subbase', subbaseDesignInputs, true) ?? 0).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>m</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                )}
            </Paper>
        );
    };

    // Unused render functions removed

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [aashtoAccordionExpanded, setAashtoAccordionExpanded] = useState<boolean>(false);

    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // qui setto la dimensione forzata del tire layers standard degli strati
    // Larghezze coerenti con Results.tsx (unpaved)
    // ...dichiarazioni duplicate rimosse...
    // NB: se serve la logica textile, va gestita separatamente
    // TODO: devo passare anche la X
    const [unstabilizedStageHeight, setUnstabilizedStageHeight] = useState<number | undefined>(undefined);

    // Larghezze coerenti con Results.tsx (unpaved)
    // NB: rimuovere eventuali dichiarazioni precedenti di queste variabili!
    const rectWidth = 360;
    const rectWidthGrid = 200;
    // 210 è il label + a occhio quello che occupa la quota e il numero
    const forcedStageWidth = containerWidth > 0 ? rectWidth + 210 : undefined;
    const forcedStageWidthGrid = 290; // ex: 320

    // Function to collect traffic data for PDF export
    const collectTrafficData = useCallback(() => {
        if (!aashtoResults?.detailedCalculations) return undefined;

        const { esalFactors } = aashtoResults.detailedCalculations;
        const passesFirstYear = projectData?.trafficLoad?.esalFinalValue || esalFactors.esalUnstabilised;
        const tbrRatio = esalFactors.esalUnstabilised > 0 ? esalFactors.esalStabilised / esalFactors.esalUnstabilised : 1;
        const increasedPasses = tbrRatio * passesFirstYear;

        return {
            unstabilized: {
                passesFirstYear: passesFirstYear,
                w18: esalFactors.logW18Unstabilised,
                totalEsal: projectData?.trafficLoad?.esalFinalValue ?? esalFactors.esalUnstabilised,
            },
            increasedPassages: {
                passesFirstYear: increasedPasses,
                newW18: esalFactors.logW18Stabilised,
                tbr: esalFactors.tbr,
            },
            optimized: {
                passesFirstYear: passesFirstYear,
                w18: esalFactors.logW18Unstabilised,
                thicknessSaving: pavedStabilizedGraphicData?.savingPercentage,
            },
        };
    }, [aashtoResults, projectData, pavedStabilizedGraphicData]);

    // PDF Export handlers
    const handleExportPavedPdf = useCallback(async () => {
        if (!aashtoResults || !dynamicProject.data) {
            logError('Results_Paved', 'Missing data for PDF export', undefined, LogTag.PDF_EXPORT);
            setPdfExportError(t('pdf_missing_data_error', 'Missing required data for PDF export'));
            return;
        }

        if (isExportingPdf) {
            logDebug('Results_Paved', 'PDF export already in progress', undefined, LogTag.PDF_EXPORT);
            return;
        }

        try {
            setIsExportingPdf(true);
            setPdfExportError(null);
            logDebug('Results_Paved', 'Starting paved PDF export...', undefined, LogTag.PDF_EXPORT);

            // Ensure we have captured images
            if (Object.keys(capturedCanvasImages).length === 0) {
                logDebug('Results_Paved', 'No captured images available, triggering capture...', undefined, LogTag.PDF_EXPORT);
                await capturePavedImages();
            }

            const PavedPDFDocument = (await import('../../../components/PDF/v2/paved/ResultsPdfV2')).default;

            const blob = await pdf(
                <PavedPDFDocument
                    output={aashtoResults}
                    projectInfo={{
                        projectName: dynamicProject.data?.projectName || 'N/A',
                        nrProject: dynamicProject.data?.projectInfo?.data?.nrProject || 'N/A',
                        unit: dynamicProject.data?.unit || 'Metric',
                        projectStatus: 'Active',
                        projectType: dynamicProject.data?.type || 'paved',
                        userId: dynamicProject.data?.idUser || 0,
                        appId: dynamicProject.data?.idApp || 0,
                        lastUpdateDate: new Date(),
                        owner: dynamicProject.data?.owner || 'N/A',
                        designerFirmName: dynamicProject.data?.designerFirmName || 'N/A',
                        designerFirmCountry: dynamicProject.data?.designerFirmCountry || 'N/A',
                        designerFirmCity: dynamicProject.data?.projectInfo?.data?.designerFirmCity || 'N/A',
                        coordinates: [undefined, undefined] as [number | undefined, number | undefined],
                        productCountry: dynamicProject.data?.productCountry || null,
                        products: dynamicProject.data?.products || [],
                        notes: dynamicProject.data?.notes || '',
                        mapImage: dynamicProject.data?.mapImage || '',
                    }}
                    dynamicProject={dynamicProject}
                    tireImageUnreinforced={capturedCanvasImages.tireImageUnreinforced}
                    tireImageReinforced={capturedCanvasImages.tireImageReinforced}
                    tireImageGeogrid={capturedCanvasImages.tireImageGeogrid}
                    chartImage={capturedCanvasImages.chartImage}
                    t={t}
                    trafficData={collectTrafficData()}
                />
            ).toBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${dynamicProject.data?.projectName || 'paved-project'}-results.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            logDebug('Results_Paved', 'Paved PDF export completed successfully', undefined, LogTag.PDF_EXPORT);
            setPdfExportSuccess(t('pdf_export_success', 'PDF exported successfully!'));
        } catch (error) {
            logError('Results_Paved', 'Error exporting paved PDF:', error, LogTag.PDF_EXPORT);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            setPdfExportError(t('pdf_export_error', 'Failed to export PDF: {{error}}', { error: errorMessage }));
        } finally {
            setIsExportingPdf(false);
        }
    }, [aashtoResults, dynamicProject, capturedCanvasImages, capturePavedImages, isExportingPdf, collectTrafficData, t]);

    const handleExportPavedGeogridPdf = useCallback(async () => {
        if (!aashtoResults || !dynamicProject.data || currentProjectType !== EProjectType.paved) {
            logError('Results_Paved', 'Missing data for geogrid PDF export', undefined, LogTag.PDF_EXPORT);
            setPdfExportError(t('pdf_missing_data_error', 'Missing required data for PDF export'));
            return;
        }

        // Check if geogrid is enabled
        const hasGeogrid =
            (dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridBaseCourse?.geogrids?.length || 0) > 0 ||
            (dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridSubBaseCourse?.geogrids?.length || 0) > 0;

        if (!hasGeogrid) {
            logDebug('Results_Paved', 'No geogrid configuration found for PDF export', undefined, LogTag.PDF_EXPORT);
            setPdfExportError(t('pdf_no_geogrid_error', 'No geogrid configuration found for PDF export'));
            return;
        }

        if (isExportingGeogridPdf) {
            logDebug('Results_Paved', 'Geogrid PDF export already in progress', undefined, LogTag.PDF_EXPORT);
            return;
        }

        try {
            setIsExportingGeogridPdf(true);
            setPdfExportError(null);
            logDebug('Results_Paved', 'Starting paved geogrid PDF export...', undefined, LogTag.PDF_EXPORT);

            // Ensure we have captured images
            if (Object.keys(capturedCanvasImages).length === 0) {
                logDebug('Results_Paved', 'No captured images available, triggering capture...', undefined, LogTag.PDF_EXPORT);
                await capturePavedImages();
            }

            // Legacy PDF V1 components removed - using V2 unified system
            // const PavedGeogridPDFDocument = (await import('../../../components/PDF/v2/paved/GeoGridResultsPdfV2')).default;

            // Legacy PDF V1 export functionality commented out - using V2 unified system
            throw new Error('Legacy PDF export not available - please use V2 unified system');
            /*
            const blob = await pdf(
                <PavedGeogridPDFDocument
                    projectInfo={{
                        projectName: dynamicProject.data?.projectName || 'N/A',
                        nrProject: dynamicProject.data?.projectInfo?.data?.nrProject || 'N/A',
                        unit: dynamicProject.data?.unit || 'Metric',
                        projectStatus: 'Active',
                        projectType: dynamicProject.data?.type || 'paved',
                        userId: dynamicProject.data?.idUser || 0,
                        appId: dynamicProject.data?.idApp || 0,
                        lastUpdateDate: new Date(),
                        owner: dynamicProject.data?.owner || 'N/A',
                        designerFirmName: dynamicProject.data?.designerFirmName || 'N/A',
                        designerFirmCountry: dynamicProject.data?.designerFirmCountry || 'N/A',
                        designerFirmCity: dynamicProject.data?.projectInfo?.data?.designerFirmCity || 'N/A',
                        coordinates: [undefined, undefined] as [number | undefined, number | undefined],
                        productCountry: dynamicProject.data?.productCountry || null,
                        products: dynamicProject.data?.products || [],
                        notes: dynamicProject.data?.notes || '',
                        mapImage: dynamicProject.data?.mapImage || '',
                    }}
                    projectInputs={projectData}
                    aashtoResults={aashtoResults}
                    geoGridData={dynamicProject.data?.projectInfo?.data?.geoGrid || null}
                    projectType={dynamicProject.data?.type || 'paved'}
                    unitSystem={dynamicProject.data?.unit || unitEnum.SI}
                    geogridImage={capturedCanvasImages.geogridImage}
                    t={t}
                    getProjectTypeDisplayName={getProjectTypeDisplayName}
                    projectMapImage={capturedCanvasImages.projectMapImage}
                />
            ).toBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${dynamicProject.data?.projectName || 'paved-project'}-geogrid-results.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            logDebug('Results_Paved', 'Paved geogrid PDF export completed successfully', undefined, LogTag.PDF_EXPORT);
            setPdfExportSuccess(t('pdf_geogrid_export_success', 'Geogrid PDF exported successfully!'));
            */
        } catch (error) {
            logError('Results_Paved', 'Error exporting paved geogrid PDF:', error, LogTag.PDF_EXPORT);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            setPdfExportError(t('pdf_export_error', 'Failed to export PDF: {{error}}', { error: errorMessage }));
        } finally {
            setIsExportingGeogridPdf(false);
        }
    }, [aashtoResults, dynamicProject, capturedCanvasImages, capturePavedImages, currentProjectType, isExportingGeogridPdf, t]);

    // ...existing code...
    // Rimuovo eventuali altre dichiarazioni di rectWidth, rectWidthGrid, forcedStageWidth più in basso nel file
    return (
        <Grid container id="results-component-container" ref={containerRef}>
            {currentProjectType === EProjectType.paved && (
                <Accordion expanded={aashtoAccordionExpanded} onChange={() => setAashtoAccordionExpanded(!aashtoAccordionExpanded)} sx={{ width: '100%', display: 'none' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">AASHTO &rsquo;93 Pavement Design Results</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {renderPavedResults()}

                        {/* PDF Export buttons */}
                        {aashtoResults && (
                            <Box sx={{ mt: 3 }}>
                                {/* Error display */}
                                {pdfExportError && (
                                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPdfExportError(null)}>
                                        {pdfExportError}
                                    </Alert>
                                )}

                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleExportPavedPdf}
                                        startIcon={isExportingPdf ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                                        disabled={!aashtoResults.detailedCalculations || isExportingPdf || isExportingGeogridPdf}>
                                        {isExportingPdf ? t('exporting_pdf', 'Exporting PDF...') : t('export_pdf', 'Export PDF')}
                                    </Button>

                                    {/* Show geogrid PDF button if geogrid is configured */}
                                    {((dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridBaseCourse?.geogrids?.length || 0) > 0 ||
                                        (dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridSubBaseCourse?.geogrids?.length || 0) > 0) && (
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={handleExportPavedGeogridPdf}
                                            startIcon={isExportingGeogridPdf ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                                            disabled={!aashtoResults.detailedCalculations || isExportingPdf || isExportingGeogridPdf}>
                                            {isExportingGeogridPdf ? t('exporting_geogrid_pdf', 'Exporting Geogrid PDF...') : t('export_geogrid_pdf', 'Export Geogrid PDF')}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* For paved projects: show unstabilized and stabilized diagrams */}
            {currentProjectType === EProjectType.paved && (
                <Grid container direction="row" alignItems="flex-start" justifyContent="center">
                    {/* Unstabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom>
                            {t('unstabilised_title', 'UNSTABILISED')}
                        </Typography>
                        <div id="tire-layer-unstabilized" className="cont-grafico" style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {pavedUnstabilizedGraphicData ? (
                                <TireAndLayers
                                    canvasId="tire-layer-unstabilized"
                                    stageType="unstabilized"
                                    surfaceLayer={pavedUnstabilizedGraphicData.surfaceLayer}
                                    baseLayer={pavedUnstabilizedGraphicData.baseLayer}
                                    subbaseLayer={pavedUnstabilizedGraphicData.subbaseLayer}
                                    subgrade={pavedUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={pavedUnstabilizedGraphicData.rightDimensionValue}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    showGeogrid={!!pavedUnstabilizedGraphicData.geogrid}
                                    geogrid={pavedUnstabilizedGraphicData.geogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={pavedUnstabilizedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={false}
                                    rectStartXOffset={-90}
                                    onStageHeightCalculated={h => setUnstabilizedStageHeight(h)}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Information Display under first diagram */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, mx: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Traffic Analysis
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                    maximumFractionDigits: 0,
                                                }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: ESAL (always shown) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">ESAL:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {esalFinalValue?.toLocaleString('de-DE', { maximumFractionDigits: 0 }) ?? 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Grid>
                    {/* Placeholder for second diagram (if needed) */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom>
                            {t('traffic_increase_title', 'TRAFFIC INCREASE')}
                        </Typography>
                        <div
                            id="tire-layer-increased-passages"
                            className="cont-grafico"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {pavedUnstabilizedGraphicData ? (
                                <TireAndLayers
                                    canvasId="tire-layer-increased-passages"
                                    stageType="increased-passages"
                                    surfaceLayer={pavedUnstabilizedGraphicData.surfaceLayer}
                                    baseLayer={pavedUnstabilizedGraphicData.baseLayer}
                                    subbaseLayer={pavedUnstabilizedGraphicData.subbaseLayer}
                                    subgrade={pavedUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={pavedUnstabilizedGraphicData.rightDimensionValue}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    geogrid={pavedIncreasedPassagesGraphicData?.geogrid}
                                    subbaseGeogrid={pavedIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showGeogrid={!!pavedIncreasedPassagesGraphicData?.geogrid}
                                    showSubbaseGeogrid={!!pavedIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={pavedUnstabilizedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    onStageHeightCalculated={h => setUnstabilizedStageHeight(h)}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Benefit Information Display under second diagram */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, mx: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#e8f5e8', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Traffic Benefit Analysis
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages with TBR multiplier (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(
                                                    (aashtoResults.detailedCalculations.esalFactors.esalStabilised / aashtoResults.detailedCalculations.esalFactors.esalUnstabilised) *
                                                    aashtoResults.detailedCalculations.esalFactors.esalUnstabilised
                                                ).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: TBR (always shown) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">TBR:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                            {aashtoResults.detailedCalculations.esalFactors.tbr.toFixed(1)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Grid>
                    {/* Stabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom>
                            {t('stabilised_title', 'STABILISED')}
                        </Typography>
                        <div
                            id="tire-layer-geogrid"
                            className="cont-grafico"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', border: '0px solid #eee', minHeight: '300px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {pavedStabilizedGraphicData ? (
                                <TireAndLayers
                                    canvasId="tire-layer-geogrid"
                                    stageType="geogrid"
                                    unstabilizedStageHeight={unstabilizedStageHeight}
                                    surfaceLayer={pavedStabilizedGraphicData.surfaceLayer}
                                    baseLayer={pavedStabilizedGraphicData.baseLayer}
                                    subbaseLayer={pavedStabilizedGraphicData.subbaseLayer}
                                    subgrade={pavedStabilizedGraphicData.subgrade}
                                    rightDimensionValue={pavedStabilizedGraphicData.rightDimensionValue}
                                    savingPercentage={pavedStabilizedGraphicData.savingPercentage}
                                    geogrid={pavedIncreasedPassagesGraphicData?.geogrid}
                                    subbaseGeogrid={pavedIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={true}
                                    showGeogrid={!!pavedIncreasedPassagesGraphicData?.geogrid}
                                    showSubbaseGeogrid={!!pavedIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={pavedStabilizedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    // forcedStageWidth={290}
                                    rectWidth={rectWidthGrid}
                                    alignFromBottom={true}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Information Display under third diagram - Same traffic as first but with reduced thickness */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, mx: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fff3e0', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Optimized Design
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                    maximumFractionDigits: 0,
                                                }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: Thickness Saving (always shown) */}
                                    {pavedStabilizedGraphicData?.savingPercentage && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Thickness Saving:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6f00' }}>
                                                {pavedStabilizedGraphicData.savingPercentage.toFixed(1)}%
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            )}

            {/* Surface Design Diagrams - Solo se Surface Design è abilitato */}
            {currentProjectType === EProjectType.paved && currentProjectMethod === 'asphalt' && isSurfaceDesignEnabled && (
                <Grid container direction="row" alignItems="flex-start" justifyContent="center" sx={{ mt: 3 }}>
                    {/* Unstabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {t('unstabilised_title', 'UNSTABILISED')}
                        </Typography>
                        <div
                            id="surface-design-unstabilized"
                            className="cont-grafico"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {surfaceDesignUnstabilizedGraphicData ? (
                                <TireAndLayers
                                    stageType="surface-design-unstabilized"
                                    surfaceLayer={surfaceDesignUnstabilizedGraphicData.surfaceLayer}
                                    baseLayer={surfaceDesignUnstabilizedGraphicData.baseLayer}
                                    subbaseLayer={surfaceDesignUnstabilizedGraphicData.subbaseLayer}
                                    subgrade={surfaceDesignUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={surfaceDesignUnstabilizedGraphicData.rightDimensionValue}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    showGeogrid={false}
                                    showSubbaseGeogrid={false}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={surfaceDesignUnstabilizedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Information Display under Surface Design Unstabilized */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Surface Design - Original
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                    maximumFractionDigits: 0,
                                                }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: ESAL (always shown) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">ESAL:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {esalFinalValue?.toLocaleString('de-DE', { maximumFractionDigits: 0 }) ?? 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Grid>

                    {/* Stabilized with Savings */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {t('traffic_increase_title', 'TRAFFIC INCREASE')}
                        </Typography>
                        <div
                            id="surface-design-increased-passages"
                            className="cont-grafico"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {surfaceDesignStabilizedGraphicData && surfaceDesignUnstabilizedGraphicData ? (
                                <TireAndLayers
                                    stageType="surface-design-increased-passages"
                                    surfaceLayer={surfaceDesignUnstabilizedGraphicData.surfaceLayer}
                                    baseLayer={surfaceDesignUnstabilizedGraphicData.baseLayer}
                                    subbaseLayer={surfaceDesignUnstabilizedGraphicData.subbaseLayer}
                                    subgrade={surfaceDesignUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={surfaceDesignUnstabilizedGraphicData.rightDimensionValue}
                                    geogrid={surfaceDesignIncreasedPassagesGraphicData?.geogrid}
                                    subbaseGeogrid={surfaceDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    showGeogrid={!!surfaceDesignIncreasedPassagesGraphicData?.geogrid}
                                    showSubbaseGeogrid={!!surfaceDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={surfaceDesignUnstabilizedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Benefit Information Display under Surface Design Increased Passages */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#e8f5e8', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Surface Design - Traffic Increase
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages with TBR multiplier (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(
                                                    (aashtoResults.detailedCalculations.esalFactors.esalStabilised / aashtoResults.detailedCalculations.esalFactors.esalUnstabilised) *
                                                    aashtoResults.detailedCalculations.esalFactors.esalUnstabilised
                                                ).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: TBR (always shown) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">TBR:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                            {aashtoResults.detailedCalculations.esalFactors.tbr.toFixed(1)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Grid>

                    {/* Increased Passages with Geogrids */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {t('stabilised_title', 'STABILISED')}
                        </Typography>
                        <div id="surface-design-stabilized" className="cont-grafico" style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '300px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {surfaceDesignIncreasedPassagesGraphicData ? (
                                <TireAndLayers
                                    stageType="surface-design-stabilized"
                                    surfaceLayer={surfaceDesignIncreasedPassagesGraphicData.surfaceLayer}
                                    baseLayer={surfaceDesignIncreasedPassagesGraphicData.baseLayer}
                                    subbaseLayer={surfaceDesignIncreasedPassagesGraphicData.subbaseLayer}
                                    subgrade={surfaceDesignIncreasedPassagesGraphicData.subgrade}
                                    rightDimensionValue={surfaceDesignIncreasedPassagesGraphicData.rightDimensionValue}
                                    savingPercentage={surfaceDesignStabilizedGraphicData?.savingPercentage}
                                    geogrid={surfaceDesignIncreasedPassagesGraphicData.geogrid}
                                    subbaseGeogrid={surfaceDesignIncreasedPassagesGraphicData.subbaseGeogrid}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={true}
                                    showGeogrid={!!surfaceDesignIncreasedPassagesGraphicData.geogrid}
                                    showSubbaseGeogrid={!!surfaceDesignIncreasedPassagesGraphicData.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={surfaceDesignIncreasedPassagesGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                    alignFromBottom={true}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Optimized Design Information Display under Surface Design Stabilized */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fff3e0', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Surface Design - Optimized
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                    maximumFractionDigits: 0,
                                                }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: Thickness Saving (always shown) */}
                                    {surfaceDesignStabilizedGraphicData?.savingPercentage && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Thickness Saving:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6f00' }}>
                                                {surfaceDesignStabilizedGraphicData.savingPercentage.toFixed(1)}%
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            )}

            {/* Base Design Diagrams - Solo se Base Design è abilitato */}
            {currentProjectType === EProjectType.paved && currentProjectMethod === 'asphalt' && isBaseDesignEnabled && (
                <Grid container direction="row" alignItems="flex-start" justifyContent="center" sx={{ mt: 3 }}>
                    {/* Unstabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {t('unstabilised_title', 'UNSTABILISED')}
                        </Typography>
                        <div id="base-design-unstabilized" className="cont-grafico" style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '400px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {baseDesignUnstabilizedGraphicData ? (
                                <TireAndLayers
                                    stageType="base-design-unstabilized"
                                    surfaceLayer={baseDesignUnstabilizedGraphicData.surfaceLayer}
                                    baseLayer={baseDesignUnstabilizedGraphicData.baseLayer}
                                    subbaseLayer={baseDesignUnstabilizedGraphicData.subbaseLayer}
                                    subgrade={baseDesignUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={baseDesignUnstabilizedGraphicData.rightDimensionValue}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    showGeogrid={false}
                                    showSubbaseGeogrid={false}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={baseDesignUnstabilizedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Information Display under Base Design Unstabilized */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Base Design - Original
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                    maximumFractionDigits: 0,
                                                }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: ESAL (always shown) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">ESAL:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {esalFinalValue?.toLocaleString('de-DE', { maximumFractionDigits: 0 }) ?? 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Grid>

                    {/* Increased Passages */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {t('traffic_increase_title', 'TRAFFIC INCREASE')}
                        </Typography>
                        <div
                            id="base-design-increased-passages"
                            className="cont-grafico"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '400px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {baseDesignStabilizedGraphicData && baseDesignUnstabilizedGraphicData ? (
                                <TireAndLayers
                                    stageType="base-design-increased-passages"
                                    surfaceLayer={baseDesignUnstabilizedGraphicData.surfaceLayer}
                                    baseLayer={baseDesignUnstabilizedGraphicData.baseLayer}
                                    subbaseLayer={baseDesignUnstabilizedGraphicData.subbaseLayer}
                                    subgrade={baseDesignUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={baseDesignUnstabilizedGraphicData.rightDimensionValue}
                                    geogrid={baseDesignIncreasedPassagesGraphicData?.geogrid}
                                    subbaseGeogrid={baseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    showGeogrid={!!baseDesignIncreasedPassagesGraphicData?.geogrid}
                                    showSubbaseGeogrid={!!baseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={baseDesignUnstabilizedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Benefit Information Display under Base Design Increased Passages */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#e8f5e8', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Base Design - Traffic Increase
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages with TBR multiplier (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(
                                                    (aashtoResults.detailedCalculations.esalFactors.esalStabilised / aashtoResults.detailedCalculations.esalFactors.esalUnstabilised) *
                                                    aashtoResults.detailedCalculations.esalFactors.esalUnstabilised
                                                ).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: TBR (always shown) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">TBR:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                            {aashtoResults.detailedCalculations.esalFactors.tbr.toFixed(1)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Grid>

                    {/* Stabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {t('stabilised_title', 'STABILISED')}
                        </Typography>
                        <div id="base-design-stabilized" className="cont-grafico" style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '400px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {baseDesignIncreasedPassagesGraphicData ? (
                                <TireAndLayers
                                    stageType="base-design-stabilized"
                                    surfaceLayer={baseDesignIncreasedPassagesGraphicData.surfaceLayer}
                                    baseLayer={baseDesignIncreasedPassagesGraphicData.baseLayer}
                                    subbaseLayer={baseDesignIncreasedPassagesGraphicData.subbaseLayer}
                                    subgrade={baseDesignIncreasedPassagesGraphicData.subgrade}
                                    rightDimensionValue={baseDesignIncreasedPassagesGraphicData.rightDimensionValue}
                                    savingPercentage={baseDesignStabilizedGraphicData?.savingPercentage}
                                    geogrid={baseDesignIncreasedPassagesGraphicData.geogrid}
                                    subbaseGeogrid={baseDesignIncreasedPassagesGraphicData.subbaseGeogrid}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={true}
                                    showGeogrid={!!baseDesignIncreasedPassagesGraphicData.geogrid}
                                    showSubbaseGeogrid={!!baseDesignIncreasedPassagesGraphicData.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={baseDesignIncreasedPassagesGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                    alignFromBottom={true}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Optimized Design Information Display under Base Design Stabilized */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fff3e0', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Base Design - Optimized
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                    maximumFractionDigits: 0,
                                                }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: Thickness Saving (always shown) */}
                                    {baseDesignStabilizedGraphicData?.savingPercentage && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Thickness Saving:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6f00' }}>
                                                {baseDesignStabilizedGraphicData.savingPercentage.toFixed(1)}%
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            )}

            {/* Subbase Design Diagrams - Solo se Subbase Design è abilitato */}
            {currentProjectType === EProjectType.paved && currentProjectMethod === 'asphalt' && isSubbaseDesignEnabled && (
                <Grid container direction="row" alignItems="flex-start" justifyContent="center" sx={{ mt: 3 }}>
                    {/* Unstabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {t('unstabilised_title', 'UNSTABILISED')}
                        </Typography>
                        <div
                            id="subbase-design-unstabilized"
                            className="cont-grafico"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '500px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {subbaseDesignUnstabilizedGraphicData ? (
                                <TireAndLayers
                                    stageType="subbase-design-unstabilized"
                                    surfaceLayer={subbaseDesignUnstabilizedGraphicData.surfaceLayer}
                                    baseLayer={subbaseDesignUnstabilizedGraphicData.baseLayer}
                                    subbaseLayer={subbaseDesignUnstabilizedGraphicData.subbaseLayer}
                                    subgrade={subbaseDesignUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={subbaseDesignUnstabilizedGraphicData.rightDimensionValue}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    showGeogrid={false}
                                    showSubbaseGeogrid={false}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={subbaseDesignUnstabilizedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Information Display under Subbase Design Unstabilized */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Subbase Design - Original
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                    maximumFractionDigits: 0,
                                                }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: ESAL (always shown) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">ESAL:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {esalFinalValue?.toLocaleString('de-DE', { maximumFractionDigits: 0 }) ?? 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Grid>

                    {/* Increased Passages */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {t('traffic_increase_title', 'TRAFFIC INCREASE')}
                        </Typography>
                        <div
                            id="subbase-design-increased-passages"
                            className="cont-grafico"
                            style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '500px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {subbaseDesignStabilizedGraphicData && subbaseDesignUnstabilizedGraphicData ? (
                                <TireAndLayers
                                    stageType="subbase-design-increased-passages"
                                    surfaceLayer={subbaseDesignUnstabilizedGraphicData.surfaceLayer}
                                    baseLayer={subbaseDesignUnstabilizedGraphicData.baseLayer}
                                    subbaseLayer={subbaseDesignUnstabilizedGraphicData.subbaseLayer}
                                    subgrade={subbaseDesignUnstabilizedGraphicData.subgrade}
                                    rightDimensionValue={subbaseDesignUnstabilizedGraphicData.rightDimensionValue}
                                    geogrid={subbaseDesignIncreasedPassagesGraphicData?.geogrid}
                                    subbaseGeogrid={subbaseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={false}
                                    showGeogrid={!!subbaseDesignIncreasedPassagesGraphicData?.geogrid}
                                    showSubbaseGeogrid={!!subbaseDesignIncreasedPassagesGraphicData?.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={subbaseDesignUnstabilizedGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Traffic Benefit Information Display under Subbase Design Increased Passages */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#e8f5e8', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Subbase Design - Traffic Increase
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages with TBR multiplier (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(
                                                    (aashtoResults.detailedCalculations.esalFactors.esalStabilised / aashtoResults.detailedCalculations.esalFactors.esalUnstabilised) *
                                                    aashtoResults.detailedCalculations.esalFactors.esalUnstabilised
                                                ).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: TBR (always shown) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">TBR:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                            {aashtoResults.detailedCalculations.esalFactors.tbr.toFixed(1)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Grid>

                    {/* Stabilized */}
                    <Grid item xs={12} md={4} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography align="center" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {t('stabilised_title', 'STABILISED')}
                        </Typography>
                        <div id="subbase-design-stabilized" className="cont-grafico" style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', minHeight: '500px', maxHeight: `${MAX_CANVAS_HEIGHT}px` }}>
                            {subbaseDesignIncreasedPassagesGraphicData ? (
                                <TireAndLayers
                                    stageType="subbase-design-stabilized"
                                    surfaceLayer={subbaseDesignIncreasedPassagesGraphicData.surfaceLayer}
                                    baseLayer={subbaseDesignIncreasedPassagesGraphicData.baseLayer}
                                    subbaseLayer={subbaseDesignIncreasedPassagesGraphicData.subbaseLayer}
                                    subgrade={subbaseDesignIncreasedPassagesGraphicData.subgrade}
                                    rightDimensionValue={subbaseDesignIncreasedPassagesGraphicData.rightDimensionValue}
                                    savingPercentage={subbaseDesignStabilizedGraphicData?.savingPercentage}
                                    geogrid={subbaseDesignIncreasedPassagesGraphicData.geogrid}
                                    subbaseGeogrid={subbaseDesignIncreasedPassagesGraphicData.subbaseGeogrid}
                                    showLeftDimension={false}
                                    showRightDimension={true}
                                    showSavingBox={true}
                                    showGeogrid={!!subbaseDesignIncreasedPassagesGraphicData.geogrid}
                                    showSubbaseGeogrid={!!subbaseDesignIncreasedPassagesGraphicData.subbaseGeogrid}
                                    showGeotextile={false}
                                    showTire={true}
                                    showLayers={true}
                                    unitLabel={subbaseDesignIncreasedPassagesGraphicData.unitLabel}
                                    projectType={currentProjectType}
                                    forcedStageWidth={forcedStageWidthGrid}
                                    rectWidth={rectWidthGrid}
                                    hideLayerTitles={true}
                                    rectStartXOffset={-90}
                                    alignFromBottom={true}
                                />
                            ) : (
                                <Typography>Loading diagram...</Typography>
                            )}
                        </div>

                        {/* Optimized Design Information Display under Subbase Design Stabilized */}
                        {aashtoResults?.detailedCalculations && (
                            <Box sx={{ mt: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fff3e0', minWidth: '250px' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                                    Subbase Design - Optimized
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Row 1: Number of passages (AUTOMATIC mode only) */}
                                    {esalMode === 'automatic' && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Nr. of passes in 1st year:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {(projectData?.trafficLoad?.totalPassagesPerYear || 0).toLocaleString('en-US', {
                                                    maximumFractionDigits: 0,
                                                }) ?? 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Row 2: Thickness Saving (always shown) */}
                                    {subbaseDesignStabilizedGraphicData?.savingPercentage && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Thickness Saving:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6f00' }}>
                                                {subbaseDesignStabilizedGraphicData.savingPercentage.toFixed(1)}%
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            )}

            {/* PAVED INPUTS TABLES - Separate tables per accordion section */}
            {currentProjectType === EProjectType.paved && hasAnyPavedInputData && (
                <>
                    {/* Road Information Table */}
                    {roadInfoTableData.length > 0 && (
                        <Grid item xs={12} container spacing={0} justifyContent="center" alignItems="center" className="ntk-tabella" style={{ marginBottom: '20px', marginTop: '40px' }}>
                            <Grid item xs={10}>
                                <Typography className="table-title" style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 'bold' }}>
                                    {t('road_information', 'Road Information')}
                                </Typography>
                                <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={roadInfoTableData} />
                            </Grid>
                        </Grid>
                    )}

                    {/* Traffic Load Table */}
                    {trafficLoadTableData.length > 0 && (
                        <Grid item xs={12} container spacing={0} justifyContent="center" alignItems="center" className="ntk-tabella" style={{ marginBottom: '20px', marginTop: '20px' }}>
                            <Grid item xs={10}>
                                <Typography className="table-title" style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 'bold' }}>
                                    {t('traffic_load', 'Traffic Load')}
                                </Typography>
                                <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={trafficLoadTableData} />
                            </Grid>
                        </Grid>
                    )}

                    {/* Surface Table */}
                    {surfaceTableData.length > 0 && (
                        <Grid item xs={12} container spacing={0} justifyContent="center" alignItems="center" className="ntk-tabella" style={{ marginBottom: '20px', marginTop: '20px' }}>
                            <Grid item xs={10}>
                                <Typography className="table-title" style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 'bold' }}>
                                    {t('surface', 'Surface')}
                                </Typography>
                                <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={surfaceTableData} />
                            </Grid>
                        </Grid>
                    )}

                    {/* Base Course Table */}
                    {baseCourseTableData.length > 0 && (
                        <Grid item xs={12} container spacing={0} justifyContent="center" alignItems="center" className="ntk-tabella" style={{ marginBottom: '20px', marginTop: '20px' }}>
                            <Grid item xs={10}>
                                <Typography className="table-title" style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 'bold' }}>
                                    {t('base_course', 'Base Course')}
                                </Typography>
                                <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={baseCourseTableData} />
                            </Grid>
                        </Grid>
                    )}

                    {/* Subbase Course Table */}
                    {subbaseCourseTableData.length > 0 && (
                        <Grid item xs={12} container spacing={0} justifyContent="center" alignItems="center" className="ntk-tabella" style={{ marginBottom: '20px', marginTop: '20px' }}>
                            <Grid item xs={10}>
                                <Typography className="table-title" style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 'bold' }}>
                                    {t('subbase_course', 'Subbase Course')}
                                </Typography>
                                <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={subbaseCourseTableData} />
                            </Grid>
                        </Grid>
                    )}

                    {/* Subgrade Table */}
                    {subgradeTableData.length > 0 && (
                        <Grid item xs={12} container spacing={0} justifyContent="center" alignItems="center" className="ntk-tabella" style={{ marginBottom: '20px', marginTop: '20px' }}>
                            <Grid item xs={10}>
                                <Typography className="table-title" style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 'bold' }}>
                                    {t('subgrade', 'Subgrade')}
                                </Typography>
                                <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={subgradeTableData} />
                            </Grid>
                        </Grid>
                    )}
                </>
            )}

            {/* Success notification snackbar */}
            <Snackbar open={!!pdfExportSuccess} autoHideDuration={6000} onClose={() => setPdfExportSuccess(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setPdfExportSuccess(null)} severity="success" sx={{ width: '100%' }}>
                    {pdfExportSuccess}
                </Alert>
            </Snackbar>
        </Grid>
    );
};

export default Results_Paved;
